import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiArchive,
  FiArrowDown,
  FiBell,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCornerUpLeft,
  FiFile,
  FiHeadphones,
  FiImage,
  FiInbox,
  FiLink,
  FiList,
  FiMapPin,
  FiMessageCircle,
  FiMusic,
  FiPackage,
  FiPaperclip,
  FiPlus,
  FiSearch,
  FiSmile,
  FiSend,
  FiSettings,
  FiShoppingBag,
  FiTrash2,
  FiUser,
  FiVideo,
  FiX,
  FiZap,
} from "react-icons/fi";
import { toast } from "react-toastify";
import socket from "../utilities/socket";
import { getUserFromLocalStorage } from "../utilities/user";
import { useVendorAccess } from "../hooks/useVendorAccess";
import { branchAwareMessage, isBranchRefusal } from "../utilities/vendorScope";
import HeadOfficeOnly from "../components/branch/HeadOfficeOnly";
import { isOrderPaid } from "../utilities/paymentStatus";
import TemplateActionButton from "../components/TemplateActionButton";
import {
  isEmbeddedSignupConfigured,
  launchWhatsAppEmbeddedSignup,
} from "../utilities/whatsappEmbeddedSignup";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { uploadImageToCloudinary, uploadFileToCloudinary, validateImageFile } from "../utilities/imageUpload";
import { useAutoAnimate } from "@formkit/auto-animate/react";

// WhatsApp's standard quick-reaction set (long-press reactions).
const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const QUICK_EMOJIS = [
  "👍", "🙏", "😊", "😀", "😅", "🎉", "🔥", "❤️", "✅", "🙌",
  "👋", "😍", "🤝", "💯", "😉", "🤔", "😎", "🙂", "👏", "🛵",
  "🍔", "🍗", "🥤", "🛒", "📦", "💳", "⏰", "📍", "✨", "🤷",
];
import "./OmnichannelInbox.css";
import {
  apiAssignInboxConversation,
  apiCloseInboxConversation,
  apiCompleteWhatsAppEmbeddedSignup,
  apiConvertInboxConversationToOrder,
  apiDisconnectWhatsAppChannel,
  apiCreateInboxTicket,
  apiUpdateInboxTicket,
  apiStartNewInboxOrder,
  apiConfirmInboxOrderPayment,
  apiSaveContact,
  apiFetchDispositions,
  apiCreateDisposition,
  apiDeleteDisposition,
  apiFetchAllVendorsForSuperAdmin,
  apiFetchChannelConnections,
  apiFetchInboxConversation,
  apiFetchInboxConversationCounts,
  apiFetchInboxConversations,
  apiFetchVendorInboxCatalog,
  apiSaveInboxOrderDraft,
  apiSendInboxMessage,
  apiSendInboxMenu,
  apiMarkConversationRead,
  apiReactToMessage,
  apiSendTyping,
  apiFetchWhatsAppTemplates,
  apiStartAgentConversation,
  apiFetchTemplateLibrary,
  apiSubmitTemplate,
  apiFetchInboxAutomationSettings,
  apiUpdateInboxAutomationSettings,
  INBOX_PAGE_SIZE,
} from "../api";
import AppSelect from "../components/ui/AppSelect";

// Mount the react-toastify container once (matches the pattern used across the
// dashboard, e.g. WalletDashboard / DeliveryFeeSettings). Idempotent.
// Maps a connection's real webhook status to a UI badge — never hardcoded.
const WHATSAPP_STATUS_META = {
  receiving: { label: "Active", className: "linked" },
  verified: { label: "Connected", className: "linked" },
  pending: { label: "Finishing setup…", className: "pending" },
  failed: { label: "Connection issue", className: "failed" },
};

const describeWhatsAppStatus = (connection) => {
  const status =
    connection?.webhookStatus ||
    connection?.connectionStatus ||
    (connection ? "pending" : "disconnected");
  return (
    WHATSAPP_STATUS_META[status] || { label: status, className: "pending" }
  );
};

const filterOptions = [
  { label: "All", value: "" },
  { label: "Unread", unread: true },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Lusuku App", value: "lusuku_app" },
  { label: "Unassigned", assignedTo: "unassigned" },
  { label: "Assigned to me", assignedTo: "me" },
  { label: "Open", status: "assigned" },
  { label: "Waiting", status: "waiting_for_customer" },
  { label: "Escalated", status: "escalated" },
  { label: "Pending confirmation", status: "order_pending_confirmation" },
  { label: "Order created", status: "order_created" },
  { label: "Closed", status: "closed" },
];

const statusLabel = (status) =>
  String(status || "new")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Distinct tone per conversation status so the chips/dots are visually separable.
const STATUS_TONES = {
  new: "new",
  unassigned: "new",
  assigned: "assigned",
  waiting_for_vendor: "waiting",
  waiting_for_customer: "waiting",
  linked_to_order: "order",
  order_pending_confirmation: "pending",
  order_created: "order",
  escalated: "urgent",
  resolved: "resolved",
  closed: "closed",
  spam: "urgent",
};

const statusTone = (status) => STATUS_TONES[status] || "neutral";

const ticketCategoryLabel = (category) =>
  String(category || "support")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TICKET_STATUS_LABELS = {
  open: "Open",
  assigned: "Assigned",
  escalated: "Escalated",
  resolved: "Resolved",
  closed: "Closed",
};

const ticketStatusLabel = (status) => TICKET_STATUS_LABELS[status] || ticketCategoryLabel(status);

// Last-activity time for an inbox row: time today, "Yesterday", else a short date.
const formatThreadTime = (conversation) => {
  const ts =
    conversation?.lastCustomerMessageAt ||
    conversation?.lastVendorMessageAt ||
    conversation?.updatedAt;
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const resolveVendorId = (profile) =>
  profile?.result?.parentVendorId || profile?.result?._id || profile?.result?.id;

const adminRoles = new Set(["SUPERUSER", "SUPERADMIN", "ADMIN"]);

const isPlatformAdmin = (profile) => {
  const result = profile?.result || {};
  return (
    Boolean(result.isSuperAdmin) ||
    result.userType === "LUSUKU STAFF" ||
    adminRoles.has(result.user_role) ||
    adminRoles.has(result.role)
  );
};

const ownerLabel = (conversation) => {
  const owner = conversation?.assignedToUserId;
  if (!owner) return "Unassigned";
  if (typeof owner === "string") return "Assigned";
  const fullName = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
  return owner.name || fullName || owner.businessName || owner.email || owner.phoneNumber || "Assigned";
};

// Single source of truth for the customer name shown anywhere in the inbox.
// Mirrors the backend priority (saved contact > real WhatsApp profile name >
// phone) so the list and header always agree. `displayName` is the resolved
// field the backend now sends; the rest are safe fallbacks for older cached
// payloads or brief timing gaps before the resolved field arrives.
const resolveCustomerName = (c) => {
  if (!c) return "Conversation";
  if (c.displayName) return c.displayName;
  if (c.contactName) return c.contactName;
  if (c.customerDisplayName && c.customerDisplayName !== c.customerPhone) return c.customerDisplayName;
  return c.customerPhone || "Conversation";
};

// WhatsApp-style initials palette — stable, tasteful multi-colour set. The
// colour is picked deterministically from a hash of the phone (or name) so the
// same customer always keeps the same badge colour across renders/sessions.
const AVATAR_COLORS = [
  "#0a7cff", "#00a884", "#7b6ef6", "#e9617a", "#f0883e",
  "#13c4a3", "#6d8eff", "#c956c9", "#e0a30a", "#2bb673",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6", "#26a69a",
  "#9ccc65", "#ffa726", "#8d6e63", "#78909c", "#ec407a",
  "#7e57c2", "#42a5f5", "#66bb6a", "#d4a017", "#ff7043",
  "#26c6da", "#5d4037", "#3949ab", "#00897b", "#c2185b",
];

const avatarColorFor = (seed) => {
  const str = String(seed || "?");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const avatarInitial = (name, phone) => {
  const fromName = String(name || "").trim().charAt(0);
  if (fromName) return fromName.toUpperCase();
  const fromPhone = String(phone || "").trim().charAt(0);
  if (fromPhone) return fromPhone.toUpperCase();
  return "?";
};

// Reusable customer avatar: real Lusuku photo when available, otherwise a
// deterministic-colour initials circle. `size` controls width/height/font.
const CustomerAvatar = ({ name, phone, avatarUrl, size = 40 }) => {
  const [failed, setFailed] = useState(false);
  const showImage = typeof avatarUrl === "string" && avatarUrl.trim() && !failed;
  const dimension = { width: size, height: size };

  if (showImage) {
    return (
      <span className="omni-avatar omni-avatar--photo" style={dimension}>
        <img
          src={avatarUrl}
          alt={name || phone || "Customer"}
          onError={() => setFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className="omni-avatar omni-avatar--initials"
      style={{
        ...dimension,
        background: avatarColorFor(phone || name),
        fontSize: Math.round(size * 0.42),
      }}
    >
      {avatarInitial(name, phone)}
    </span>
  );
};

const channelMeta = (channel) =>
  channel === "whatsapp"
    ? { label: "WhatsApp", icon: <FaWhatsapp /> }
    : { label: "Lusuku App", icon: <FiMessageCircle /> };

const isUnreadConversation = (conversation) => {
  if (!conversation?.lastCustomerMessageAt) return false;
  if (!conversation?.lastVendorMessageAt) return true;
  return new Date(conversation.lastCustomerMessageAt).getTime() >
    new Date(conversation.lastVendorMessageAt).getTime();
};

// Labels + size formatting for the staged-attachment preview strip.
const ATTACHMENT_KIND_LABELS = {
  image: "Photo",
  video: "Video",
  audio: "Audio",
  document: "Document",
};

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Reusable composer popover: renders a full-screen backdrop that closes the
// popover when clicked, while clicks inside the panel are preserved.
const ComposerPopover = ({ open, onClose, className = "omni-quick-replies", children }) => {
  if (!open) return null;
  return (
    <>
      <div className="omni-quick-replies-backdrop" onClick={onClose} />
      <div className={className} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </>
  );
};

const OmnichannelInbox = () => {
  const profile = getUserFromLocalStorage();
  const currentUserId = profile?.result?._id || profile?.result?.id;
  const defaultVendorId = resolveVendorId(profile);
  const platformAdmin = isPlatformAdmin(profile);
  // Only a Lusuku superadmin, the vendor account, or vendor admin/manager staff
  // may remove a WhatsApp number — agents can add/use but not remove it.
  const userRole = String(profile?.result?.user_role || profile?.result?.role || "").toUpperCase();
  const isVendorAdmin =
    String(profile?.result?._id || "") === String(defaultVendorId || "") ||
    ["ADMIN", "OWNER", "MANAGER", "SUPERADMIN"].includes(userRole);
  const canRemoveNumber = platformAdmin || isVendorAdmin;
  const [selectedVendorId, setSelectedVendorId] = useState(platformAdmin ? "" : defaultVendorId);
  const vendorId = platformAdmin ? selectedVendorId : defaultVendorId;
  const [adminVendors, setAdminVendors] = useState([]);
  // The server's own words when it refuses this login the inbox (a member
  // limited to some branches: the inbox is head office's, 4 Oct 2026).
  const [hqOnlyMessage, setHqOnlyMessage] = useState("");
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorError, setVendorError] = useState("");
  const [activeFilter, setActiveFilter] = useState(filterOptions[0]);
  // Free-text conversation finder (name or phone), applied client-side over
  // the loaded list on top of whatever filter pill is active.
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState([]);
  const [filterCounts, setFilterCounts] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  // Deep-link: open a specific conversation when arrived via ?c=<id> (e.g. from
  // the navbar WhatsApp notification icon).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get("c");
    if (cid) setSelectedId(cid);
  }, []);
  // Per-conversation detail cache. Switching chats reads from this map so we
  // never re-show the skeleton or re-fetch a conversation we already have. Each
  // entry keeps the REST shape { conversation, messages, ticket, linkedOrder }.
  const [detailsById, setDetailsById] = useState({});
  // `detail` is derived from the cache for the currently-selected conversation,
  // preserving the exact shape every existing read relies on.
  const detail = (selectedId && detailsById[selectedId]) || null;
  // Compatibility wrapper so the existing optimistic `setDetail((prev) => ...)`
  // callsites keep working — they now patch the cache entry for selectedId.
  const setDetail = useCallback(
    (updater) => {
      if (!selectedId) return;
      setDetailsById((prev) => {
        const current = prev[selectedId] || null;
        const next = typeof updater === "function" ? updater(current) : updater;
        if (next === current) return prev;
        return { ...prev, [selectedId]: next };
      });
    },
    [selectedId],
  );
  const [reply, setReply] = useState("");
  // Live mirror of the composer text so the chat-switch effect can stash the
  // outgoing conversation's draft without depending on `reply` itself.
  const replyRef = useRef("");
  useEffect(() => {
    replyRef.current = reply;
  }, [reply]);
  // Per-conversation composer drafts, keyed by conversation id. A half-typed
  // message follows its conversation instead of leaking into the next thread.
  const draftsRef = useRef({});
  // Real-WhatsApp interactions: reply/quote target, per-message reaction picker,
  // and incoming "agent typing" indicator.
  const [replyTarget, setReplyTarget] = useState(null);
  const [reactPickerId, setReactPickerId] = useState(null);
  // Full-size viewer for chat images (click a thumbnail to open).
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [typingPeer, setTypingPeer] = useState(false);
  const typingDebounceRef = useRef(null);
  const typingPeerTimerRef = useRef(null);
  const readMarkedRef = useRef(null);
  // Tracks which conversation id currently has its detail loaded, so the
  // skeleton loader only shows on first open / chat switch — never on the
  // background refreshes triggered by every inbound/outbound socket event.
  const loadedDetailIdRef = useRef(null);
  // Live refs for values the socket handlers need to read without re-binding the
  // listeners on every chat switch (keeps the socket effect's deps to [vendorId]).
  const selectedIdRef = useRef(selectedId);
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);
  const [loading, setLoading] = useState(false);
  // Live mirror of the detail cache so stable socket handlers can read the
  // latest cached docs without re-binding when the cache changes.
  const detailsByIdRef = useRef({});
  // Smoothly animates conversation rows when an updated thread moves to the top
  // of the list. `setThreadAnimate` lets us suppress the slide for the one
  // reorder we want to be INSTANT: pinning the just-selected chat to the top.
  const [threadListRef, setThreadAnimate] = useAutoAnimate();
  // auto-animate only hands back a callback ref, so we mirror the list node into
  // our own ref to scroll the freshly-pinned active chat into view at the top.
  const threadListNodeRef = useRef(null);
  const attachThreadListRef = useCallback(
    (node) => {
      threadListNodeRef.current = node;
      threadListRef(node);
    },
    [threadListRef],
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [connectionState, setConnectionState] = useState(null);
  const [actionError, setActionError] = useState("");
  const [windowExpired, setWindowExpired] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [orderForm, setOrderForm] = useState({
    customerPhone: "",
    deliveryAddress: "",
    deliveryLocation: null,
    paymentMethod: "COD",
    notesFromConversation: "",
    items: [{ productId: "", name: "", qty: 1, price: "" }],
  });
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupError, setSignupError] = useState("");
  const embeddedSignupReady = isEmbeddedSignupConfigured();
  const [ticketForm, setTicketForm] = useState({ category: "support", priority: "normal", notes: "" });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [ticketActionBusy, setTicketActionBusy] = useState(null); // status being applied
  const [ticketResolution, setTicketResolution] = useState("");
  const [orderSummary, setOrderSummary] = useState("");
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState(null);

  // Inbox automation: offline WhatsApp alerts + chat distribution strategy.
  const [automationSettings, setAutomationSettings] = useState(null);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [automationError, setAutomationError] = useState("");
  const [automationDraft, setAutomationDraft] = useState({
    enabled: false,
    whatsappNumber: "",
    templateName: "offline_chat_alert",
    languageCode: "en_US",
    cooldownMinutes: 15,
    escalationMinutes: 0,
    strategy: "round_robin",
  });

  // Resizable inbox columns (percentages; center = remainder). Persisted locally.
  const workspaceRef = useRef(null);
  const [panelWidths, setPanelWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("omniPanelWidths"));
      if (saved && saved.left && saved.right) return saved;
    } catch (e) {
      /* ignore */
    }
    return { left: 25, right: 28 };
  });

  const startPanelDrag = (which) => (event) => {
    event.preventDefault();
    const onMove = (ev) => {
      const rect = workspaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setPanelWidths((w) => {
        if (which === "left") {
          return { ...w, left: Math.min(45, Math.max(15, pct)) };
        }
        return { ...w, right: Math.min(45, Math.max(15, 100 - pct)) };
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setPanelWidths((w) => {
        try {
          localStorage.setItem("omniPanelWidths", JSON.stringify(w));
        } catch (e) {
          /* ignore */
        }
        return w;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const centerWidth = Math.max(20, 100 - panelWidths.left - panelWidths.right);

  // Mobile: show one column at a time as tabs (Chats / Conversation / Details)
  // so the chat gets the full screen with confined scrolling.
  const [mobileTab, setMobileTab] = useState("list");
  useEffect(() => {
    if (selectedId) setMobileTab("chat");
  }, [selectedId]);

  const unreadCount = useMemo(
    () => conversations.filter((c) => isUnreadConversation(c)).length,
    [conversations],
  );

  // Badge the browser tab with the unread count so replies get noticed while
  // the agent is on another tab or app.
  const baseTitleRef = useRef(typeof document !== "undefined" ? document.title : "Lusuku");
  useEffect(() => {
    const base = baseTitleRef.current || "Lusuku";
    document.title = unreadCount > 0 ? `(${unreadCount}) ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [unreadCount]);

  // The active (selected) conversation is always rendered first. The underlying
  // `conversations` state stays in updatedAt-desc order (so socket/new-message
  // reordering keeps working); we only lift the selected row to the top for
  // display. When nothing is selected, or the selected row isn't in this view,
  // the list renders unchanged.
  const orderedConversations = useMemo(() => {
    if (!selectedId) return conversations;
    const idx = conversations.findIndex((c) => String(c._id) === String(selectedId));
    if (idx <= 0) return conversations;
    const next = conversations.slice();
    const [selected] = next.splice(idx, 1);
    return [selected, ...next];
  }, [conversations, selectedId]);

  // Search filter: match the resolved display name, or the phone by digits
  // (so "0772 123" finds "+256772123..." despite formatting differences).
  const visibleConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return orderedConversations;
    const qDigits = q.replace(/\D/g, "");
    return orderedConversations.filter((c) => {
      const name = String(resolveCustomerName(c) || "").toLowerCase();
      if (name.includes(q)) return true;
      if (!qDigits) return false;
      const phone = String(c.customerPhone || "").replace(/\D/g, "");
      return phone.includes(qDigits);
    });
  }, [orderedConversations, searchQuery]);

  // Pinning the active chat to the top must feel INSTANT, not slide. Disable the
  // list animation synchronously (before paint) for the reorder caused by a new
  // selection, then re-enable on the next frame so genuine new-message moves
  // still animate smoothly.
  useLayoutEffect(() => {
    setThreadAnimate(false);
    // Reveal the just-pinned active chat by snapping the list back to the top
    // (it now sits at index 0). Instant, to match the pin.
    if (threadListNodeRef.current) threadListNodeRef.current.scrollTop = 0;
    const raf = requestAnimationFrame(() => setThreadAnimate(true));
    return () => cancelAnimationFrame(raf);
  }, [selectedId, setThreadAnimate]);

  // Composer: emoji palette + image attachment
  const [showEmoji, setShowEmoji] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // A picked-but-not-yet-sent attachment: { file, kind, previewUrl }. Staged so
  // the agent can preview/cancel and add a caption before anything uploads.
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageScrollRef = useRef(null);
  // Whether the agent is currently reading near the bottom of the thread.
  // Updated on every scroll, and read (not re-measured) when a new message
  // lands — after the DOM grows, a live measurement would always say "far".
  const nearBottomRef = useRef(true);
  const [showJumpPill, setShowJumpPill] = useState(false);
  // Drives a fade-in: messages stay hidden until they're positioned at the bottom.
  const [chatReady, setChatReady] = useState(false);

  const handleMessageScroll = () => {
    const el = messageScrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    nearBottomRef.current = near;
    if (near) setShowJumpPill(false);
  };

  const jumpToLatest = () => {
    nearBottomRef.current = true;
    setShowJumpPill(false);
    messagesEndRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  };

  // Re-hide ONLY while the detail for the newly-selected chat isn't available
  // yet (first open / hard reload). For an already-cached conversation `detail`
  // is present synchronously on the very render the id changes, so we must NOT
  // blank it — otherwise the reveal below has nothing to un-blank and the panel
  // gets stuck at opacity:0. Hiding here is also a plain effect (passive phase),
  // so it can race the reveal; the passive fallback at the end of this block is
  // the guarantee that visibility always resolves true once detail exists.
  const messageCount = detail?.messages?.length || 0;
  const detailReady = !detailLoading && Boolean(detail);
  useEffect(() => {
    // Only blank the panel when we genuinely have nothing to show yet.
    if (!detailReady) setChatReady(false);
  }, [selectedId, detailReady]);
  useEffect(() => {
    if (detailLoading) setChatReady(false);
  }, [detailLoading]);

  // Scroll to the most recent message BEFORE the browser paints, so the user
  // never sees the list jump from top to bottom. Then reveal with a fade.
  // While the agent is scrolled up reading history, a new message must NOT
  // yank the view to the bottom — keep their position and offer a jump pill.
  // Own sends always stick (the agent just acted), as does a chat switch.
  const scrollConvRef = useRef(null);
  useLayoutEffect(() => {
    if (detailLoading) return;
    const isNewConversation = scrollConvRef.current !== selectedId;
    scrollConvRef.current = selectedId;
    if (isNewConversation) {
      nearBottomRef.current = true;
      setShowJumpPill(false);
    }
    const msgs = detail?.messages || [];
    const lastIsOwn = msgs.length > 0 && msgs[msgs.length - 1]?.direction === "outbound";
    if (isNewConversation || lastIsOwn || nearBottomRef.current) {
      const node = messagesEndRef.current;
      if (node) node.scrollIntoView({ block: "end" });
      nearBottomRef.current = true;
      setShowJumpPill(false);
    } else if (msgs.length > 0) {
      setShowJumpPill(true);
    }
    setChatReady(true);
  }, [selectedId, messageCount, detailLoading]);

  // Robust visibility fallback. The layout effect above is the fragile, timing-
  // sensitive path (it only re-runs when selectedId / messageCount / detailLoading
  // change, and can be clobbered by the passive resets above on a cached switch
  // where detailLoading never toggles). This passive effect runs AFTER those
  // resets in the same commit and unconditionally reveals the track whenever the
  // detail is loaded — so the message panel can NEVER stay blank while
  // `detail.messages` is present, regardless of scroll/ordering races.
  useEffect(() => {
    if (detailReady) setChatReady(true);
  }, [selectedId, messageCount, detailReady]);

  const insertEmoji = (emoji) => {
    setReply((prev) => `${prev || ""}${emoji}`);
  };

  // #5 Send a single product of interest into the chat (image + name + price).
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [pickedLocation, setPickedLocation] = useState(null);
  const [sendingLocation, setSendingLocation] = useState(false);

  // Composer popovers are mutually exclusive: opening one closes the others, so
  // a single click on any icon switches directly (no double-click to dismiss).
  const togglePopover = (name) => {
    const willOpen = {
      quick: name !== "quick" ? false : !showQuickReplies,
      emoji: name !== "emoji" ? false : !showEmoji,
      product: name !== "product" ? false : !showProductPicker,
      location: name !== "location" ? false : !showLocationPicker,
    };
    setShowQuickReplies(willOpen.quick);
    setShowEmoji(willOpen.emoji);
    setShowProductPicker(willOpen.product);
    setShowLocationPicker(willOpen.location);
    if (willOpen.product) loadCatalog();
    if (!willOpen.location) setPickedLocation(null);
  };
  // Agent-started (template-initiated) conversation modal.
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [templates, setTemplates] = useState([]);
  // Submitted-but-not-yet-approved templates (PENDING/REJECTED). They are
  // invisible to the approved-only WABA fetch, so the backend surfaces them
  // separately as `pendingSubmissions`; we show them in the picker (with a
  // status pill, non-selectable) so a vendor sees their submission is in review.
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVars, setTemplateVars] = useState([]);
  const [startingChat, setStartingChat] = useState(false);
  const [newChatError, setNewChatError] = useState("");
  // Curated starter-template library.
  const [showLibrary, setShowLibrary] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryExisting, setLibraryExisting] = useState({});
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [submittingKey, setSubmittingKey] = useState(null);
  const [libraryNote, setLibraryNote] = useState("");
  const [sendingProduct, setSendingProduct] = useState(false);
  const [sendingMenu, setSendingMenu] = useState(false);

  // Send the WHOLE menu as a WhatsApp interactive list: the customer browses,
  // taps a product, picks options + quantity, and their selection lands back
  // here as an order draft (inbox:order_draft_saved) — no retyping.
  const sendMenuToCustomer = async () => {
    if (!selectedId || sendingMenu) return;
    try {
      setActionError("");
      setSendingMenu(true);
      await apiSendInboxMenu(vendorId, selectedId);
      setShowProductPicker(false);
      await loadDetail();
      await loadConversations({ silent: true });
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED") {
        setWindowExpired(true);
      } else {
        setActionError(error.response?.data?.message || "Could not send the menu.");
      }
    } finally {
      setSendingMenu(false);
    }
  };

  const sendProductToChat = async (product) => {
    if (!selectedId || !product) return;
    const priceLabel = product.price != null ? ` — UGX ${Number(product.price).toLocaleString()}` : "";
    const caption = `${product.name}${priceLabel}`;
    try {
      setActionError("");
      setSendingProduct(true);
      const payload = product.imageUrl
        ? { media: { type: "image", link: product.imageUrl }, body: caption }
        : { body: caption };
      await apiSendInboxMessage(vendorId, selectedId, payload);
      setShowProductPicker(false);
      await loadDetail();
      await loadConversations({ silent: true });
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED") {
        setWindowExpired(true);
      } else {
        setActionError(error.response?.data?.message || "Could not send product.");
      }
    } finally {
      setSendingProduct(false);
    }
  };

  // Contacts: save/edit a name for the customer's number
  const [editingContact, setEditingContact] = useState(false);
  const [contactNameInput, setContactNameInput] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  const startEditContact = () => {
    setContactNameInput(selectedConversation?.contactName || selectedConversation?.customerDisplayName || "");
    setEditingContact(true);
  };

  const saveContact = async () => {
    if (!contactNameInput.trim() || !selectedConversation?.customerPhone) return;
    try {
      setSavingContact(true);
      setActionError("");
      await apiSaveContact(vendorId, {
        phone: selectedConversation.customerPhone,
        name: contactNameInput.trim(),
      });
      const savedName = contactNameInput.trim();
      const savedPhone = selectedConversation.customerPhone;
      // Optimistically reflect the saved name immediately so the header and
      // list update without waiting for the refetch below. The refetch still
      // runs to reconcile with the server's resolved values.
      setDetail((prev) =>
        prev?.conversation && prev.conversation.customerPhone === savedPhone
          ? { ...prev, conversation: { ...prev.conversation, contactName: savedName, displayName: savedName } }
          : prev,
      );
      setConversations((prev) =>
        prev.map((c) =>
          c.customerPhone === savedPhone
            ? { ...c, contactName: savedName, displayName: savedName }
            : c,
        ),
      );
      setEditingContact(false);
      await loadDetail();
      await loadConversations();
    } catch (error) {
      setActionError(error.response?.data?.message || "Could not save contact.");
    } finally {
      setSavingContact(false);
    }
  };

  const docInputRef = useRef(null);
  const replyInputRef = useRef(null);

  // Auto-grow the reply box up to a max height (then it scrolls), like WhatsApp,
  // so long messages stay fully visible instead of being pushed out of view.
  useLayoutEffect(() => {
    const el = replyInputRef.current;
    if (!el) return;
    // Empty: clear the inline height so the box collapses to its CSS one-line
    // height. Otherwise a stale measurement (e.g. taken while the panel was
    // briefly narrow) can leave the empty composer stuck tall.
    if (!reply) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [reply]);
  // Picking a file STAGES it (preview strip above the composer) instead of
  // uploading and sending on the spot. The agent sees exactly what will go
  // out, can cancel, and can add a caption — nothing leaves until Send.
  const stageAttachment = (file, kind) => {
    if (!file) return;
    let previewUrl = null;
    try {
      previewUrl = URL.createObjectURL(file);
    } catch (e) {
      previewUrl = null;
    }
    setActionError("");
    setUploadProgress(0);
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return { file, kind, previewUrl };
    });
  };

  const cancelAttachment = () => {
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
    setUploadProgress(0);
  };

  const handleAttachDoc = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    stageAttachment(file, "document");
  };

  const handleAttach = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const mime = String(file.type || "");
    const kind = mime.startsWith("video/") ? "video" : mime.startsWith("audio/") ? "audio" : "image";
    if (kind === "image") {
      const valid = validateImageFile(file);
      if (!valid.valid) {
        setActionError(valid.error || "Unsupported file.");
        return;
      }
    }
    stageAttachment(file, kind);
  };

  // Upload the staged attachment, then send it with the composer text as its
  // caption. Video rides the video-enabled unsigned preset; images keep the
  // compressed image pipeline; audio/documents go through the auto endpoint.
  const sendAttachment = async (caption) => {
    const staged = pendingAttachment;
    if (!staged || attaching || !selectedId) return;
    const { file, kind } = staged;
    // Pin the target conversation: if the agent switches threads mid-upload,
    // the send still lands where they hit Send, and we don't clobber the
    // other thread's restored draft afterwards.
    const targetConvId = selectedId;
    try {
      setActionError("");
      setAttaching(true);
      setUploadProgress(0);
      let result;
      if (kind === "image") {
        result = await uploadImageToCloudinary(file, {
          preset: "lusuku-clou",
          onProgress: setUploadProgress,
        });
      } else {
        result = await uploadFileToCloudinary(file, {
          preset: kind === "video" ? "lusuku-video" : "lusuku-clou",
          onProgress: setUploadProgress,
        });
      }
      const media = { type: kind, link: result.secure_url, mime: file.type };
      if (kind === "document") media.filename = file.name;
      await apiSendInboxMessage(vendorId, targetConvId, {
        media,
        body: caption || undefined,
      });
      if (String(selectedIdRef.current) === String(targetConvId)) {
        cancelAttachment();
        setReply("");
        setReplyTarget(null);
      }
      loadDetailFor(targetConvId);
      loadConversations({ silent: true });
      loadConversationCounts();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED") {
        setWindowExpired(true);
      } else {
        // Surface the real upload/send failure (e.g. Cloudinary rejecting a
        // format the preset doesn't allow) instead of a generic shrug.
        setActionError(error.response?.data?.message || error.message || "Attachment could not be sent.");
      }
    } finally {
      setAttaching(false);
      setUploadProgress(0);
    }
  };

  // Dispositions / quick replies
  const [dispositions, setDispositions] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [newDisposition, setNewDisposition] = useState({ title: "", body: "" });

  const loadDispositions = useCallback(async () => {
    if (!vendorId) return;
    try {
      const { data } = await apiFetchDispositions(vendorId);
      setDispositions(data.dispositions || []);
    } catch (e) {
      /* non-fatal */
    }
  }, [vendorId]);

  useEffect(() => {
    loadDispositions();
  }, [loadDispositions]);

  const insertDisposition = (body) => {
    setReply((prev) => (prev && prev.trim() ? `${prev.trim()} ${body}` : body));
    setShowQuickReplies(false);
  };

  const addDisposition = async () => {
    if (!newDisposition.title.trim() || !newDisposition.body.trim()) return;
    try {
      await apiCreateDisposition(vendorId, newDisposition);
      setNewDisposition({ title: "", body: "" });
      await loadDispositions();
    } catch (error) {
      setActionError(error.response?.data?.message || "Could not save quick reply.");
    }
  };

  const removeDisposition = async (id) => {
    try {
      await apiDeleteDisposition(vendorId, id);
      await loadDispositions();
    } catch (e) {
      /* non-fatal */
    }
  };

  const params = useMemo(() => {
    const next = {};
    if (activeFilter.value) next.channel = activeFilter.value;
    if (activeFilter.unread) next.unread = "true";
    if (activeFilter.status) next.status = activeFilter.status;
    if (activeFilter.assignedTo) next.assignedTo = activeFilter.assignedTo;
    return next;
  }, [activeFilter]);

  const loadConnections = useCallback(async () => {
    if (!vendorId) return;
    const { data } = await apiFetchChannelConnections(vendorId);
    setConnectionState(data);
  }, [vendorId]);

  const loadAutomationSettings = useCallback(async () => {
    if (!vendorId) return;
    try {
      const { data } = await apiFetchInboxAutomationSettings(vendorId);
      setAutomationSettings(data);
    } catch (err) {
      // Non-fatal: pill simply renders as "off" until the endpoint responds.
    }
  }, [vendorId]);

  // `silent` skips the list spinner. Initial mount / filter changes are loud;
  // every socket / enrichment-triggered reload is silent so the list never flashes.
  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!vendorId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await apiFetchInboxConversations(vendorId, { ...params, limit: INBOX_PAGE_SIZE });
      const list = data.conversations || [];
      if (silent) {
        // Background refresh: MERGE the fresh first page over what's loaded
        // instead of replacing it — a replace silently threw away every page
        // the agent had fetched via "Load more" whenever a new conversation
        // arrived.
        setConversations((prev) => {
          const seen = new Set(list.map((c) => String(c._id)));
          const tail = prev.filter((c) => !seen.has(String(c._id)));
          return [...list, ...tail];
        });
      } else {
        setConversations(list);
      }
      setHasMore(list.length >= INBOX_PAGE_SIZE);
      if (!selectedId && list[0]?._id) {
        setSelectedId(list[0]._id);
      }
    } catch (error) {
      if (!isBranchRefusal(error)) throw error;
      setHqOnlyMessage(branchAwareMessage(error));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [params, selectedId, vendorId]);

  const loadConversationCounts = useCallback(async () => {
    if (!vendorId) return;
    const { data } = await apiFetchInboxConversationCounts(vendorId);
    setFilterCounts(data.counts || {});
  }, [vendorId]);

  const loadMoreConversations = useCallback(async () => {
    if (!vendorId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const cursor = conversations[conversations.length - 1]?.updatedAt;
      const { data } = await apiFetchInboxConversations(vendorId, {
        ...params,
        limit: INBOX_PAGE_SIZE,
        before: cursor,
      });
      const next = data.conversations || [];
      setConversations((prev) => [...prev, ...next]);
      setHasMore(next.length >= INBOX_PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [conversations, hasMore, loadingMore, params, vendorId]);

  // Fetch (or silently revalidate) a conversation's detail and store it in the
  // cache. Skeleton shows ONLY when there is no cached entry for `id` yet — a
  // cached conversation revalidates in the background with no loader flash.
  const loadDetailFor = useCallback(
    async (id) => {
      if (!vendorId || !id) return;
      const isFirstLoad = !detailsById[id];
      if (isFirstLoad && String(id) === String(selectedIdRef.current)) setDetailLoading(true);
      try {
        const { data } = await apiFetchInboxConversation(vendorId, id);
        setDetailsById((prev) => ({ ...prev, [id]: data }));
        loadedDetailIdRef.current = id;
      } finally {
        if (isFirstLoad && String(id) === String(selectedIdRef.current)) setDetailLoading(false);
      }
    },
    [vendorId, detailsById],
  );

  const loadDetail = useCallback(async () => {
    await loadDetailFor(selectedId);
  }, [loadDetailFor, selectedId]);

  // Warm the detail cache in the background so a later chat switch reads from
  // cache and never shows the skeleton. Silent for non-selected ids (loadDetailFor
  // only flips detailLoading for the id that is actually selected). The live
  // `detailsByIdRef` guard means a hover/refetch never re-requests a cached chat.
  const prefetchDetail = useCallback(
    (id) => {
      if (!id || detailsByIdRef.current[id]) return;
      loadDetailFor(id);
    },
    [loadDetailFor],
  );

  // Prefetch the top few conversations once per list (keyed on the first row's
  // id) so the first switch — before the user has hovered anything — is instant.
  const prefetchedTopRef = useRef("");
  useEffect(() => {
    const firstId = conversations[0]?._id ? String(conversations[0]._id) : "";
    if (!firstId || prefetchedTopRef.current === firstId) return;
    prefetchedTopRef.current = firstId;
    conversations.slice(0, 6).forEach((c) => prefetchDetail(c._id));
  }, [conversations, prefetchDetail]);

  // ---- Socket payload patching helpers ----------------------------------
  // These mutate state in place from the rich socket envelopes instead of the
  // old blanket "refetch everything" approach. Stable callbacks so the socket
  // effect can depend on [vendorId] alone and never re-bind on chat switch.

  // Sort newest-first, matching the REST list order (updatedAt desc).
  const sortByUpdatedDesc = useCallback((list) => {
    return [...list].sort(
      (a, b) => new Date(b?.updatedAt || 0).getTime() - new Date(a?.updatedAt || 0).getTime(),
    );
  }, []);

  // Merge a (possibly un-enriched) conversation doc into the list by _id. For a
  // never-before-seen id we prepend it AND do a single silent list reload to
  // pull the REST enrichment (display name + populated lastMessageId). Known ids
  // are patched in place (existing enriched fields preserved) and re-sorted so
  // the touched conversation animates to the top.
  const upsertConversation = useCallback(
    (conv) => {
      if (!conv?._id) return;
      setConversations((prev) => {
        const idx = prev.findIndex((c) => String(c._id) === String(conv._id));
        if (idx === -1) {
          // Unknown id: prepend now for instant feedback, then enrich silently.
          loadConversations({ silent: true });
          return sortByUpdatedDesc([conv, ...prev]);
        }
        const merged = { ...prev[idx], ...conv };
        const next = prev.slice();
        next[idx] = merged;
        return sortByUpdatedDesc(next);
      });
    },
    [loadConversations, sortByUpdatedDesc],
  );

  // Append/replace a message inside a cached conversation's detail, deduped by
  // _id. Only touches the cache for conversations we've already fetched.
  const upsertMessage = useCallback((conversationId, message) => {
    if (!conversationId || !message?._id) return;
    setDetailsById((prev) => {
      const entry = prev[conversationId];
      if (!entry) return prev; // not cached -> nothing to patch (list still updates)
      const messages = entry.messages || [];
      const idx = messages.findIndex((m) => String(m._id) === String(message._id));
      const nextMessages =
        idx === -1
          ? [...messages, message]
          : messages.map((m) => (String(m._id) === String(message._id) ? { ...m, ...message } : m));
      return { ...prev, [conversationId]: { ...entry, messages: nextMessages } };
    });
  }, []);

  // Patch arbitrary fields onto a cached conversation detail entry (ticket,
  // linkedOrder, orderDraft, etc.).
  const patchDetailEntry = useCallback((conversationId, patch) => {
    if (!conversationId) return;
    setDetailsById((prev) => {
      const entry = prev[conversationId];
      if (!entry) return prev;
      return { ...prev, [conversationId]: { ...entry, ...patch } };
    });
  }, []);

  // Patch a single message's reaction inside a cached conversation.
  const patchMessageReaction = useCallback((conversationId, messageId, reactionEmoji) => {
    if (!conversationId || !messageId) return;
    setDetailsById((prev) => {
      const entry = prev[conversationId];
      if (!entry) return prev;
      const messages = (entry.messages || []).map((m) =>
        String(m._id) === String(messageId) ? { ...m, reactionEmoji } : m,
      );
      return { ...prev, [conversationId]: { ...entry, messages } };
    });
  }, []);

  // Clear the unread state for a conversation in the list (mark-read echo).
  const markConversationReadLocal = useCallback((conversationId, lastReadAt) => {
    if (!conversationId) return;
    setConversations((prev) =>
      prev.map((c) =>
        String(c._id) === String(conversationId)
          ? {
              ...c,
              lastReadAt: lastReadAt || new Date().toISOString(),
              // Bump the vendor read marker past the customer's last message so
              // isUnreadConversation() resolves to "read" without a refetch.
              lastVendorMessageAt:
                c.lastCustomerMessageAt || c.lastVendorMessageAt || new Date().toISOString(),
            }
          : c,
      ),
    );
  }, []);

  // Keep the live mirror of the detail cache up to date for socket handlers.
  useEffect(() => {
    detailsByIdRef.current = detailsById;
  }, [detailsById]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    loadAutomationSettings();
  }, [loadAutomationSettings]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadConversationCounts();
  }, [loadConversationCounts]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!vendorId) return undefined;

    socket.emit("joinVendorInbox", { vendorId });

    // Each handler consumes the backend envelope { event, conversationId,
    // payload, ... } and patches state in place — no blanket refetch, no list
    // spinner flash. Counts are refreshed silently (cheap, no spinner).
    const refreshCounts = () => loadConversationCounts();

    const onMessage = (envelope) => {
      const p = envelope?.payload || {};
      if (p.conversation) upsertConversation(p.conversation);
      const convId = envelope?.conversationId || p.conversation?._id;
      if (p.message) upsertMessage(convId, p.message);
      // Keep the cached DETAIL conversation fresh too. The 24h-window banner is
      // derived from selectedConversation.lastCustomerMessageAt — patching only
      // the list left the open thread with a stale copy, so the banner stayed
      // on "window closed" even after the customer had just replied.
      if (p.conversation && convId) {
        const cached = detailsByIdRef.current[convId];
        if (cached?.conversation) {
          patchDetailEntry(convId, { conversation: { ...cached.conversation, ...p.conversation } });
        }
      }
      const inbound = p.message?.direction === "inbound";
      if (inbound && convId && String(convId) === String(selectedIdRef.current)) {
        // A reply landing in the OPEN chat must not leave it flagged unread
        // until the agent clicks away and back (the mount-time mark-read is
        // guarded to once per open) — re-mark read immediately.
        apiMarkConversationRead(vendorId, convId)
          .then(() => markConversationReadLocal(convId))
          .catch(() => {});
      } else if (inbound && convId) {
        // Message for a thread the agent is NOT looking at: surface it with a
        // clickable toast that jumps straight to that conversation.
        const name = resolveCustomerName(p.conversation);
        const rawPreview = p.message.body || `[${String(p.message.messageType || "message")}]`;
        const preview = rawPreview.length > 90 ? `${rawPreview.slice(0, 90)}…` : rawPreview;
        toast.info(`${name}: ${preview}`, {
          toastId: `inbox-new-${convId}`,
          onClick: () => setSelectedId(convId),
        });
      }
      refreshCounts();
    };

    const onAssignedOrClosed = (envelope) => {
      const p = envelope?.payload || {};
      if (p.conversation) upsertConversation(p.conversation);
      refreshCounts();
    };

    const onTicket = (envelope) => {
      const p = envelope?.payload || {};
      if (p.conversation) upsertConversation(p.conversation);
      const convId = envelope?.conversationId || p.conversation?._id;
      if (p.ticket) patchDetailEntry(convId, { ticket: p.ticket });
    };

    const onOrderCreated = (envelope) => {
      const p = envelope?.payload || {};
      if (p.conversation) upsertConversation(p.conversation);
      const convId = envelope?.conversationId || p.conversation?._id;
      if (p.order) patchDetailEntry(convId, { linkedOrder: p.order });
    };

    const onOrderDraftSaved = (envelope) => {
      const p = envelope?.payload || {};
      if (p.conversation) upsertConversation(p.conversation);
      // The draft lives on the conversation (selectedConversation.orderDraft);
      // upsertConversation already carries it. Patch the cached doc too if present.
      const convId = envelope?.conversationId || p.conversation?._id;
      if (p.draft) {
        const cachedConv = detailsByIdRef.current[convId]?.conversation || p.conversation;
        patchDetailEntry(convId, { conversation: { ...cachedConv, orderDraft: p.draft } });
      }
    };

    const onRead = (envelope) => {
      const p = envelope?.payload || {};
      const convId = envelope?.conversationId || p.conversationId;
      markConversationReadLocal(convId, p.lastReadAt);
      refreshCounts();
    };

    const onReacted = (envelope) => {
      const p = envelope?.payload || {};
      const convId = envelope?.conversationId || p.conversationId;
      patchMessageReaction(convId, p.messageId, p.reactionEmoji);
    };

    // Another agent (not me) typing on the currently-open conversation. Reads
    // live refs so the listener never needs rebinding on chat switch.
    const onAgentTyping = (envelope) => {
      if (String(envelope?.conversationId) !== String(selectedIdRef.current)) return;
      if (String(envelope?.payload?.userId) === String(currentUserIdRef.current)) return;
      setTypingPeer(true);
      clearTimeout(typingPeerTimerRef.current);
      typingPeerTimerRef.current = setTimeout(() => setTypingPeer(false), 6000);
    };

    // Meta finished reviewing a submitted template. Like every other inbox event,
    // the backend wraps the data in an envelope ({ event, vendorId, conversationId,
    // payload }); the real fields live on envelope.payload:
    // { vendorId, templateName, language, status, category, reason }. Live-flip the
    // matching template's status (PENDING -> APPROVED/REJECTED) and toast.
    const onTemplateStatus = (envelope = {}) => {
      const { templateName, language, status, reason } = envelope?.payload || {};
      if (!templateName) return;
      const normalized = String(status || "").toUpperCase();
      // A template name can exist in several locales (e.g. en_US + fr_FR); Meta
      // reviews each language separately, so only flip the row whose language
      // matches. Older payloads omit `language` — in that case fall back to the
      // legacy name-only match so we don't silently drop the update.
      const incomingLang = String(language || "");
      const matches = (t) =>
        t.name === templateName &&
        (!incomingLang || String(t.language || "") === incomingLang);

      // Flip the status pill in the curated library list (matched by name + language).
      setLibrary((prev) =>
        prev.map((t) => (matches(t) ? { ...t, submissionStatus: normalized } : t)),
      );
      // existingByName is keyed by name only (it predates per-language tracking).
      // Patch it when the language matches the stored entry, or when either side
      // lacks language info — never overwrite a different locale's record.
      setLibraryExisting((prev) => {
        const current = prev[templateName];
        const storedLang = String(current?.language || "");
        if (current && incomingLang && storedLang && storedLang !== incomingLang) {
          return prev;
        }
        return {
          ...prev,
          [templateName]: {
            name: templateName,
            ...(current || {}),
            ...(incomingLang ? { language: incomingLang } : {}),
            submissionStatus: normalized,
            status: normalized,
          },
        };
      });
      // Mirror onto the approved-templates picker if that template is listed.
      setTemplates((prev) =>
        prev.map((t) => (matches(t) ? { ...t, submissionStatus: normalized } : t)),
      );
      // Live-flip the pill on the pending-submissions row in the New-chat picker.
      setPendingTemplates((prev) =>
        prev.map((t) => (matches(t) ? { ...t, submissionStatus: normalized } : t)),
      );

      if (normalized === "APPROVED") {
        toast.success(`Template '${templateName}' was approved`);
      } else if (normalized === "REJECTED") {
        toast.warn(
          reason
            ? `⚠️ Template '${templateName}' was rejected: ${reason}`
            : `⚠️ Template '${templateName}' was rejected`,
        );
      } else {
        toast.info(`Template '${templateName}' status: ${normalized || "updated"}`);
      }
    };

    socket.on("inbox:message_received", onMessage);
    socket.on("inbox:message_sent", onMessage);
    socket.on("inbox:conversation_assigned", onAssignedOrClosed);
    socket.on("inbox:conversation_closed", onAssignedOrClosed);
    socket.on("inbox:ticket_created", onTicket);
    socket.on("inbox:ticket_updated", onTicket);
    socket.on("inbox:order_created", onOrderCreated);
    socket.on("inbox:order_draft_saved", onOrderDraftSaved);
    socket.on("inbox:conversation_read", onRead);
    socket.on("inbox:message_reacted", onReacted);
    socket.on("inbox:agent_typing", onAgentTyping);
    socket.on("inbox:template_status", onTemplateStatus);

    return () => {
      socket.emit("leaveVendorInbox", { vendorId });
      socket.off("inbox:message_received", onMessage);
      socket.off("inbox:message_sent", onMessage);
      socket.off("inbox:conversation_assigned", onAssignedOrClosed);
      socket.off("inbox:conversation_closed", onAssignedOrClosed);
      socket.off("inbox:ticket_created", onTicket);
      socket.off("inbox:ticket_updated", onTicket);
      socket.off("inbox:order_created", onOrderCreated);
      socket.off("inbox:order_draft_saved", onOrderDraftSaved);
      socket.off("inbox:conversation_read", onRead);
      socket.off("inbox:message_reacted", onReacted);
      socket.off("inbox:agent_typing", onAgentTyping);
      socket.off("inbox:template_status", onTemplateStatus);
    };
    // Handlers read live state via refs / stable callbacks, so binding once per
    // vendor is enough — no re-bind churn when the agent switches chats.
  }, [
    vendorId,
    loadConversationCounts,
    upsertConversation,
    upsertMessage,
    patchDetailEntry,
    patchMessageReaction,
    markConversationReadLocal,
  ]);

  useEffect(() => {
    if (!selectedId) return undefined;
    socket.emit("joinConversation", { conversationId: selectedId });
    return () => socket.emit("leaveConversation", { conversationId: selectedId });
  }, [selectedId]);

  // Realtime hygiene: everything that happened while the socket was down is
  // lost, and nothing used to re-sync after a drop. On every (re)connect,
  // re-join the rooms and silently refetch the list, counts, and the open
  // thread. While disconnected, show a reconnecting bar instead of silently
  // serving a stale inbox.
  const [socketDown, setSocketDown] = useState(false);
  const loadDetailForRef = useRef(null);
  useEffect(() => {
    loadDetailForRef.current = loadDetailFor;
  }, [loadDetailFor]);
  useEffect(() => {
    if (!vendorId) return undefined;
    const onConnect = () => {
      setSocketDown(false);
      socket.emit("joinVendorInbox", { vendorId });
      if (selectedIdRef.current) {
        socket.emit("joinConversation", { conversationId: selectedIdRef.current });
        if (loadDetailForRef.current) loadDetailForRef.current(selectedIdRef.current);
      }
      loadConversations({ silent: true });
      loadConversationCounts();
    };
    const onDisconnect = () => setSocketDown(true);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (!socket.connected) setSocketDown(true);
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [vendorId, loadConversations, loadConversationCounts]);

  // Opening a conversation marks it read (clears the unread badge) even if the
  // agent never replies — some messages need no reply. Once per open.
  useEffect(() => {
    if (!selectedId || !vendorId || !detail) return;
    if (readMarkedRef.current === selectedId) return;
    readMarkedRef.current = selectedId;
    const convId = selectedId;
    apiMarkConversationRead(vendorId, convId)
      .then(() => {
        // Patch the unread badge away in place (no list spinner). Counts are
        // cheap and have no spinner, so refresh them quietly. The server's
        // conversation_read echo is handled by onRead, which also just patches.
        markConversationReadLocal(convId);
        loadConversationCounts();
      })
      .catch(() => {});
  }, [selectedId, vendorId, detail, markConversationReadLocal, loadConversationCounts]);

  // Chat switch is a hard composer reset: stash the outgoing thread's draft,
  // restore the incoming thread's one, and drop every piece of cross-thread
  // state — quoted-reply target, reaction picker, staged attachment, typing
  // dots. Without this, a quote or attachment picked in customer A's chat
  // could be sent into customer B's thread.
  const draftConvRef = useRef(null);
  useEffect(() => {
    const prevId = draftConvRef.current;
    if (prevId && prevId !== selectedId) {
      draftsRef.current[prevId] = replyRef.current || "";
    }
    draftConvRef.current = selectedId;
    setReply(selectedId ? draftsRef.current[selectedId] || "" : "");
    setReplyTarget(null);
    setReactPickerId(null);
    setTypingPeer(false);
    setUploadProgress(0);
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, [selectedId]);

  const loadCatalog = useCallback(async () => {
    if (!vendorId) return;
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const { data } = await apiFetchVendorInboxCatalog(vendorId);
      setCatalog(data.products || []);
    } catch (error) {
      setCatalogError(error.response?.data?.message || "Could not load products. Tap to retry.");
    } finally {
      setCatalogLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!platformAdmin) return undefined;
    let mounted = true;

    setVendorLoading(true);
    setVendorError("");
    apiFetchAllVendorsForSuperAdmin()
      .then(({ data }) => {
        if (!mounted) return;
        setAdminVendors(data.vendors || []);
      })
      .catch((error) => {
        if (!mounted) return;
        setVendorError(error.response?.data?.message || "Vendors could not be loaded.");
      })
      .finally(() => {
        if (mounted) setVendorLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [platformAdmin]);

  useEffect(() => {
    setSelectedId(null);
    setDetailsById({}); // drop the per-conversation detail cache for the old vendor
    loadedDetailIdRef.current = null;
    setConversations([]);
    setFilterCounts({});
    setConnectionState(null);
    setCatalog([]);
    setActionError("");
    setWindowExpired(false);
  }, [vendorId]);

  const selectedConversation = detail?.conversation;
  // The already-loaded inbox-list row for the selected chat. It carries the
  // resolved name/phone/avatar/status, so the header can render fully from it
  // INSTANTLY on switch — before the detail fetch returns — and never flashes a
  // blank/phone-only header while loading.
  const selectedListRow = useMemo(
    () => conversations.find((c) => String(c._id) === String(selectedId)) || null,
    [conversations, selectedId],
  );
  // Single source for header fields: prefer the freshly fetched detail, fall
  // back to the list row while it loads.
  const headerConversation = selectedConversation || selectedListRow;
  // Resolve the customer's display name for the header. Prefer a real name
  // (saved contact or WhatsApp profile name) over the raw phone number, and
  // fall back to the matching inbox-list row — which already carries the name —
  // when the freshly fetched detail only has the phone. Phone is the last resort.
  const headerCustomerName = (() => {
    // A resolved name only counts as "real" if it's not just the phone number.
    const realName = (c) => {
      const n = resolveCustomerName(c);
      return n && n !== "Conversation" && n !== c?.customerPhone ? n : null;
    };
    return (
      realName(selectedConversation) ||
      realName(selectedListRow) ||
      selectedConversation?.customerPhone ||
      selectedListRow?.customerPhone ||
      "Conversation"
    );
  })();
  const headerCustomerPhone = selectedConversation?.customerPhone || selectedListRow?.customerPhone || "";
  const headerAvatarUrl = selectedConversation?.customerAvatarUrl || selectedListRow?.customerAvatarUrl || null;
  // Show the ticket card through the whole lifecycle; only a fully "closed"
  // ticket falls back to the create form (so a new issue can be raised).
  const activeTicket = detail?.ticket && detail.ticket.status !== "closed"
    ? detail.ticket
    : null;

  const createTicket = async () => {
    if (!selectedId || ticketSubmitting) return;
    setTicketSubmitting(true);
    setTicketError("");
    try {
      await apiCreateInboxTicket(vendorId, selectedId, ticketForm);
      setTicketForm({ category: "support", priority: "normal", notes: "" });
      await loadDetail();
    } catch (error) {
      setTicketError(error.response?.data?.message || "Could not create the ticket. Please try again.");
    } finally {
      setTicketSubmitting(false);
    }
  };

  // Advance a ticket through its lifecycle (assign / escalate / resolve / close /
  // reopen). Resolve & Close carry the optional resolution note.
  const updateTicket = async (status) => {
    if (!selectedId || !activeTicket?._id || ticketActionBusy) return;
    setTicketActionBusy(status);
    setTicketError("");
    try {
      const payload = ["resolved", "closed"].includes(status) && ticketResolution.trim()
        ? { status, notes: ticketResolution.trim() }
        : { status };
      await apiUpdateInboxTicket(vendorId, selectedId, activeTicket._id, payload);
      setTicketResolution("");
      await loadDetail();
      await loadConversations();
    } catch (error) {
      setTicketError(error.response?.data?.message || "Could not update the ticket. Please try again.");
      // Re-sync from the server so a stale card (e.g. a ticket already advanced
      // by an earlier action) corrects itself instead of offering invalid moves.
      loadDetail();
    } finally {
      setTicketActionBusy(null);
    }
  };

  const activeWhatsApp = connectionState?.whatsapp?.[0];

  // ---- Inbox automation helpers ----
  const offlineAlert = automationSettings?.offlineAlert;
  const offlineAlertsOn = Boolean(offlineAlert?.enabled);
  const maskWhatsAppNumber = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    return `…${digits.slice(-5)}`;
  };
  const draftNumberDigits = automationDraft.whatsappNumber.replace(/\D/g, "");
  const draftNumberValid = draftNumberDigits.length >= 9;
  const automationSaveBlocked = automationDraft.enabled && !draftNumberValid;

  const openAutomationModal = () => {
    const alert = automationSettings?.offlineAlert || {};
    const distribution = automationSettings?.distribution || {};
    setAutomationDraft({
      enabled: Boolean(alert.enabled),
      whatsappNumber: alert.whatsappNumber || "",
      templateName: alert.templateName || "offline_chat_alert",
      languageCode: alert.languageCode || "en_US",
      cooldownMinutes:
        typeof alert.cooldownMinutes === "number" ? alert.cooldownMinutes : 15,
      escalationMinutes:
        typeof alert.escalationMinutes === "number" ? alert.escalationMinutes : 0,
      strategy: distribution.strategy || "round_robin",
    });
    setAutomationError("");
    setAutomationModalOpen(true);
  };

  const saveAutomationSettings = async () => {
    if (!vendorId || automationSaveBlocked) return;
    setSavingAutomation(true);
    setAutomationError("");
    try {
      const payload = {
        offlineAlert: {
          enabled: automationDraft.enabled,
          whatsappNumber: automationDraft.whatsappNumber.trim(),
          templateName: automationDraft.templateName,
          languageCode: automationDraft.languageCode,
          cooldownMinutes: Number(automationDraft.cooldownMinutes) || 15,
          escalationMinutes: Math.max(
            0,
            Math.floor(Number(automationDraft.escalationMinutes) || 0),
          ),
        },
        distribution: { strategy: automationDraft.strategy },
      };
      const { data } = await apiUpdateInboxAutomationSettings(vendorId, payload);
      setAutomationSettings(data);
      setAutomationModalOpen(false);
    } catch (error) {
      setAutomationError(error.response?.data?.message || "Could not save settings.");
    } finally {
      setSavingAutomation(false);
    }
  };

  const canCreateOrder = Boolean(selectedId && !selectedConversation?.linkedOrderId);
  const savedDraft = selectedConversation?.orderDraft;
  const hasDraft =
    selectedConversation?.status === "order_pending_confirmation" ||
    Boolean(savedDraft?.items?.length);
  const draftItems = orderForm.items.filter((item) => item.name && item.name.trim());

  useEffect(() => {
    setActionError("");
    setWindowExpired(false);
    setSummaryDirty(false);
    setOrderSummary("");
    const draft = selectedConversation?.orderDraft;
    if (draft?.items?.length) {
      setOrderForm({
        customerPhone: draft.customerPhone || selectedConversation?.customerPhone || "",
        deliveryAddress: draft.deliveryAddress || "",
        paymentMethod: draft.paymentMethod || "COD",
        notesFromConversation: draft.notesFromConversation || "",
        items: draft.items.map((item) => ({
          productId: item.productId || "",
          name: item.name || "",
          qty: item.qty || 1,
          price: item.price ?? "",
        })),
      });
    } else {
      setOrderForm((current) => ({
        ...current,
        customerPhone: selectedConversation?.customerPhone || "",
        notesFromConversation: selectedConversation?.lastMessageId?.body || "",
        items: [{ productId: "", name: "", qty: 1, price: "" }],
      }));
    }
  }, [selectedConversation?._id]);

  // Keep the 24h-window banner TRUTHFUL: derive it from the same fact the
  // backend enforces (lastCustomerMessageAt), instead of only latching on a
  // failed send. This (a) clears the banner the moment the customer replies —
  // it used to stay stuck on "window closed" after a template re-engage got a
  // response — and (b) shows it proactively on stale threads before the agent
  // wastes a send. A template send alone does NOT reopen the window (WhatsApp
  // policy): only a fresh customer message flips this back off.
  useEffect(() => {
    if (!selectedConversation || selectedConversation.channel !== "whatsapp") return;
    const last = selectedConversation.lastCustomerMessageAt;
    if (!last) return; // brand-new agent-started thread: keep the latched state
    const windowOpen = Date.now() - new Date(last).getTime() < 24 * 60 * 60 * 1000;
    setWindowExpired(!windowOpen);
  }, [selectedConversation?._id, selectedConversation?.channel, selectedConversation?.lastCustomerMessageAt]);

  const orderTotal = orderForm.items.reduce(
    (sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0),
    0,
  );

  const buildOrderSummary = useCallback(() => {
    const lines = orderForm.items
      .filter((item) => item.name && item.name.trim())
      .map(
        (item) =>
          `• ${Number(item.qty || 1)} × ${item.name.trim()} — UGX ${Number(
            item.price || 0,
          ).toLocaleString()}`,
      );
    const total = orderForm.items.reduce(
      (sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0),
      0,
    );
    const resolvedName = selectedConversation ? resolveCustomerName(selectedConversation) : "";
    // Don't greet with a placeholder or the raw phone number.
    const name =
      resolvedName && resolvedName !== "Conversation" && resolvedName !== selectedConversation?.customerPhone
        ? resolvedName
        : "";
    return [
      `Hi${name ? ` ${name}` : ""}, here's your order summary:`,
      "",
      ...lines,
      "",
      `Total: UGX ${total.toLocaleString()}`,
      orderForm.paymentMethod ? `Payment: ${orderForm.paymentMethod}` : "",
      orderForm.deliveryAddress ? `Deliver to: ${orderForm.deliveryAddress}` : "",
      "",
      "Please reply to confirm and we'll place your order.",
    ]
      .filter((line) => line !== null && line !== undefined)
      .join("\n");
  }, [orderForm, selectedConversation]);

  const effectiveSummary = summaryDirty ? orderSummary : buildOrderSummary();

  // Notify the customer (and other agents) that this agent is typing — debounced
  // so each keystroke doesn't fire a request. The Cloud API shows our typing to
  // the customer for ~25s; it does not report the customer's own typing back.
  const handleReplyChange = (value) => {
    setReply(value);
    if (!selectedId || !value.trim()) return;
    if (typingDebounceRef.current) return; // already sent recently
    apiSendTyping(vendorId, selectedId).catch(() => {});
    typingDebounceRef.current = setTimeout(() => {
      typingDebounceRef.current = null;
    }, 8000);
  };

  const handleReact = async (message, emoji) => {
    setReactPickerId(null);
    if (!selectedId) return;
    const next = message.reactionEmoji === emoji ? "" : emoji; // toggle off if same
    // Optimistic update so the chip flips instantly.
    setDetail((prev) =>
      prev
        ? { ...prev, messages: (prev.messages || []).map((m) => (m._id === message._id ? { ...m, reactionEmoji: next } : m)) }
        : prev
    );
    try {
      await apiReactToMessage(vendorId, selectedId, message._id, next);
    } catch (error) {
      await loadDetail(); // revert to server truth on failure
    }
  };

  const sendLocation = async () => {
    if (!selectedId || !pickedLocation?.latLng) return;
    const lat = pickedLocation.latLng.lat;
    const lng = pickedLocation.latLng.lng;
    if (lat == null || lng == null) return;
    setSendingLocation(true);
    try {
      await apiSendInboxMessage(vendorId, selectedId, {
        location: {
          latitude: lat,
          longitude: lng,
          name: pickedLocation.locationName || undefined,
          address: pickedLocation.address || undefined,
        },
      });
      setShowLocationPicker(false);
      setPickedLocation(null);
      loadDetail();
      loadConversations({ silent: true });
    } catch (error) {
      setActionError(error.response?.data?.message || "Could not send location.");
    } finally {
      setSendingLocation(false);
    }
  };

  const openNewChat = async () => {
    setShowNewChat(true);
    setNewChatError("");
    setSelectedTemplate(null);
    setTemplateVars([]);
    // Always refetch on open so submission statuses (and any newly-approved or
    // just-submitted templates) are current — template status changes over time.
    setTemplatesLoading(true);
    setTemplatesError("");
    try {
      const { data } = await apiFetchWhatsAppTemplates(vendorId);
      setTemplates(data.templates || []);
      setPendingTemplates(data.pendingSubmissions || []);
      if (data.reason) setTemplatesError(data.reason);
    } catch (error) {
      setTemplatesError(error.response?.data?.message || "Could not load templates.");
      setPendingTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const pickTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setTemplateVars(new Array(tpl?.variableCount || 0).fill(""));
  };

  const startChat = async () => {
    if (!newChatPhone.trim() || !selectedTemplate) return;
    setStartingChat(true);
    setNewChatError("");
    try {
      const { data } = await apiStartAgentConversation(vendorId, {
        toPhone: newChatPhone.trim(),
        templateName: selectedTemplate.name,
        languageCode: selectedTemplate.language,
        bodyParams: templateVars,
        bodyText: selectedTemplate.bodyText,
      });
      setShowNewChat(false);
      setNewChatPhone("");
      setSelectedTemplate(null);
      setTemplateVars([]);
      await loadConversations();
      if (data?.conversation?._id) setSelectedId(data.conversation._id);
    } catch (error) {
      setNewChatError(error.response?.data?.message || "Could not start the conversation.");
    } finally {
      setStartingChat(false);
    }
  };

  const openTemplateLibrary = async () => {
    setShowLibrary(true);
    setLibraryNote("");
    if (library.length === 0) {
      setLibraryLoading(true);
      try {
        const { data } = await apiFetchTemplateLibrary(vendorId);
        setLibrary(data.library || []);
        setLibraryExisting(data.existingByName || {});
      } catch (error) {
        setLibraryNote(error.response?.data?.message || "Could not load the template library.");
      } finally {
        setLibraryLoading(false);
      }
    }
  };

  const submitLibraryTemplate = async (tpl) => {
    setSubmittingKey(tpl.key);
    setLibraryNote("");
    try {
      const { data } = await apiSubmitTemplate(vendorId, tpl.key);
      // Normalise the server's status onto `submissionStatus` so the row's
      // status pill (PENDING by default after a fresh submit) renders the same
      // whether it's read from this optimistic patch, a refetch, or the socket
      // event. Patch BOTH the legacy existingByName map and the library entry so
      // the pill survives a refresh and re-open.
      const submissionStatus = String(data.status || "PENDING").toUpperCase();
      setLibraryExisting((prev) => ({
        ...prev,
        [tpl.name]: { name: tpl.name, status: data.status, submissionStatus },
      }));
      setLibrary((prev) =>
        prev.map((t) => (t.key === tpl.key ? { ...t, submissionStatus } : t)),
      );
      setLibraryNote(`"${tpl.title}" submitted — Meta is reviewing it (${data.status}). It'll appear in the template list once approved.`);
    } catch (error) {
      setLibraryNote(error.response?.data?.message || "Submission failed.");
    } finally {
      setSubmittingKey(null);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    const text = reply.trim();
    if (!selectedId) return;
    // A staged attachment sends via its own path, with the text as caption.
    if (pendingAttachment) {
      await sendAttachment(text);
      return;
    }
    if (!text) return;

    // Optimistic UI: show the bubble and clear the input instantly instead of
    // waiting on the WhatsApp API round-trip + a full refetch. We reconcile
    // with the server in the background.
    const tempId = `temp-${Date.now()}`;
    const replyingTo = replyTarget;
    const optimistic = {
      _id: tempId,
      direction: "outbound",
      senderType: "agent",
      messageType: "text",
      body: text,
      status: "sending",
      createdAt: new Date().toISOString(),
      contextSummary: replyingTo
        ? { body: replyingTo.body, messageType: replyingTo.messageType, direction: replyingTo.direction }
        : null,
      optimistic: true,
    };
    setDetail((prev) => (prev ? { ...prev, messages: [...(prev.messages || []), optimistic] } : prev));
    setReply("");
    setReplyTarget(null);
    setActionError("");
    setWindowExpired(false);

    try {
      await apiSendInboxMessage(vendorId, selectedId, {
        body: text,
        replyToMessageId: replyingTo?._id || undefined,
      });
      // Reconcile in the background — non-blocking for the UI. loadDetail is
      // what retires the optimistic temp bubble (the socket echo appends the
      // real message under a different _id), so it stays. The list refetch is
      // silent: the echo already patched the row, and a loud reload here
      // flashed the whole list spinner on every send.
      loadDetail();
      loadConversations({ silent: true });
      loadConversationCounts();
    } catch (error) {
      // Drop the optimistic bubble and surface the failure / restore the draft.
      setDetail((prev) =>
        prev ? { ...prev, messages: (prev.messages || []).filter((m) => m._id !== tempId) } : prev
      );
      if (
        error.response?.status === 422 &&
        error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED"
      ) {
        setWindowExpired(true);
        setActionError("");
        setReply(text);
      } else {
        setActionError(error.response?.data?.message || "Reply could not be sent.");
        setReply(text);
      }
    }
  };

  const startEmbeddedSignup = async () => {
    setActionError("");
    setSignupError("");
    setSignupBusy(true);
    try {
      const result = await launchWhatsAppEmbeddedSignup();
      await apiCompleteWhatsAppEmbeddedSignup(vendorId, result);
      await loadConnections();
    } catch (error) {
      setSignupError(
        error.response?.data?.message ||
          error.message ||
          "WhatsApp connection could not be completed.",
      );
    } finally {
      setSignupBusy(false);
    }
  };

  const confirmDisconnectWhatsApp = async () => {
    const connection = pendingDisconnect;
    if (!connection?._id) return;
    try {
      setActionError("");
      setDisconnecting(true);
      await apiDisconnectWhatsAppChannel(vendorId, connection._id);
      await loadConnections();
      setPendingDisconnect(null);
    } catch (error) {
      setActionError(error.response?.data?.message || "WhatsApp number could not be removed.");
    } finally {
      setDisconnecting(false);
    }
  };

  const updateOrderItem = (index, key, value) => {
    setOrderForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    }));
  };

  const selectCatalogItem = (index, productId) => {
    const product = catalog.find((p) => String(p._id) === productId);
    setOrderForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, productId: productId, name: product?.name || "", price: product?.price ?? item.price }
          : item,
      ),
    }));
  };

  const addOrderItem = () => {
    setOrderForm((current) => ({
      ...current,
      items: [...current.items, { productId: "", name: "", qty: 1, price: "" }],
    }));
  };

  const removeOrderItem = (index) => {
    setOrderForm((current) => ({
      ...current,
      items: current.items.filter((item, itemIndex) => itemIndex !== index),
    }));
  };

  const collectOrderItems = () =>
    orderForm.items
      .map((item) => ({
        ...(item.productId ? { productId: item.productId } : {}),
        name: (item.name || "").trim(),
        qty: Number(item.qty || 1),
        price: Number(item.price || 0),
      }))
      .filter((item) => item.name);

  const buildOrderPayload = (items, extra = {}) => ({
    customerPhone: orderForm.customerPhone,
    deliveryAddress: orderForm.deliveryAddress || "",
    paymentMethod: orderForm.paymentMethod,
    notesFromConversation: orderForm.notesFromConversation,
    items,
    ...extra,
  });

  // Step 2 -> 3: persist the draft (status becomes order_pending_confirmation).
  const saveDraft = async (extra = {}) => {
    if (!canCreateOrder) return false;
    const items = collectOrderItems();
    if (!items.length) {
      setActionError("Add at least one item before saving the draft.");
      return false;
    }
    try {
      setActionError("");
      setSavingDraft(true);
      await apiSaveInboxOrderDraft(vendorId, selectedId, buildOrderPayload(items, extra));
      await loadDetail();
      await loadConversations();
      await loadConversationCounts();
      return true;
    } catch (error) {
      setActionError(error.response?.data?.message || "Draft could not be saved.");
      return false;
    } finally {
      setSavingDraft(false);
    }
  };

  // Step 4: send the reviewed summary to the customer, then mark it on the draft.
  const sendSummaryToCustomer = async () => {
    if (!selectedId || !effectiveSummary.trim()) return;
    try {
      setActionError("");
      setSendingSummary(true);
      await apiSendInboxMessage(vendorId, selectedId, { body: effectiveSummary.trim() });
      await saveDraft({ summarySent: true });
      await loadDetail();
    } catch (error) {
      if (
        error.response?.status === 422 &&
        error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED"
      ) {
        setWindowExpired(true);
      } else {
        setActionError(error.response?.data?.message || "Summary could not be sent.");
      }
    } finally {
      setSendingSummary(false);
    }
  };

  // Created-order view. Prefer the PERSISTED order (server-priced, authoritative)
  // — the draft's item prices are not reliable after creation. Fall back to the
  // draft only before an order exists.
  const createdOrderItems = useMemo(() => {
    const order = detail?.linkedOrder;
    if (Array.isArray(order?.orderItems) && order.orderItems.length) {
      return order.orderItems.map((it) => ({
        name: it.name || "Item",
        qty: Number(it.qty || 1),
        price: Number(it.price || 0) + Number(it.specPriceDelta || 0),
        imageUrl: it.imageUrl || null,
      }));
    }
    const draft = selectedConversation?.orderDraft;
    const items = Array.isArray(draft?.items) ? draft.items : [];
    return items.map((it) => {
      const product = catalog.find((p) => String(p._id) === String(it.productId));
      return {
        name: it.name || product?.name || "Item",
        qty: Number(it.qty || 1),
        price: Number(it.price || product?.price || 0),
        imageUrl: product?.imageUrl || null,
      };
    });
  }, [detail?.linkedOrder, selectedConversation?.orderDraft, catalog]);

  // Use the persisted order total when available (it includes any tax/fees),
  // otherwise sum the reconstructed line items.
  const itemsSum = createdOrderItems.reduce((sum, it) => sum + it.qty * it.price, 0);
  const createdOrderTotal =
    detail?.linkedOrder?.totalPrice != null && Number(detail.linkedOrder.totalPrice) > 0
      ? Number(detail.linkedOrder.totalPrice)
      : itemsSum;

  const buildCreatedOrderText = useCallback(() => {
    const lines = createdOrderItems.map(
      (it) => `• ${it.qty} × ${it.name} — UGX ${(it.qty * it.price).toLocaleString()}`,
    );
    return [
      "Order Confirmation",
      ...lines,
      `Total: UGX ${createdOrderTotal.toLocaleString()}`,
      selectedConversation?.orderDraft?.deliveryAddress
        ? `Deliver to: ${
            typeof selectedConversation.orderDraft.deliveryAddress === "object"
              ? selectedConversation.orderDraft.deliveryAddress.address
              : selectedConversation.orderDraft.deliveryAddress
          }`
        : "",
      selectedConversation?.linkedOrderId ? `Order ref: ${selectedConversation.linkedOrderId}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [createdOrderItems, createdOrderTotal, selectedConversation]);

  const [orderCopied, setOrderCopied] = useState(false);
  const copyCreatedOrder = async () => {
    try {
      await navigator.clipboard.writeText(buildCreatedOrderText());
      setOrderCopied(true);
      setTimeout(() => setOrderCopied(false), 2000);
    } catch (e) {
      setActionError("Could not copy to clipboard.");
    }
  };

  const shareCreatedOrder = async () => {
    if (!selectedId) return;
    try {
      setActionError("");
      await apiSendInboxMessage(vendorId, selectedId, { body: buildCreatedOrderText() });
      await loadDetail();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.code === "WHATSAPP_WINDOW_EXPIRED") {
        setWindowExpired(true);
      } else {
        setActionError(error.response?.data?.message || "Could not share the order details.");
      }
    }
  };

  // Payment confirmation gate.
  // Previously this reproduced the old, already-fixed server gate: it read a
  // top-level `transactionId` that does not exist on the Order schema (the real
  // one is nested at `paymentResult.transactionId`), checked only the legacy
  // snake_case `payment_status`, and never looked at `isPaid` — so an order paid
  // by card, MoMo, Airtel or collected as cash still showed "unconfirmed" here.
  // Now routed through the shared helper (see utilities/paymentStatus.js).
  const linkedOrder = detail?.linkedOrder;
  const paymentConfirmed = isOrderPaid(linkedOrder);
  const [txnInput, setTxnInput] = useState("");
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const confirmPayment = async () => {
    if (!selectedId || !txnInput.trim()) return;
    try {
      setActionError("");
      setConfirmingPayment(true);
      await apiConfirmInboxOrderPayment(vendorId, selectedId, {
        transactionId: txnInput.trim(),
        paymentMethod: linkedOrder?.paymentMethod,
      });
      setTxnInput("");
      await loadDetail();
    } catch (error) {
      setActionError(error.response?.data?.message || "Could not confirm payment.");
    } finally {
      setConfirmingPayment(false);
    }
  };

  const startNewOrder = async () => {
    if (!selectedId) return;
    try {
      setActionError("");
      await apiStartNewInboxOrder(vendorId, selectedId);
      setOrderForm({
        customerPhone: selectedConversation?.customerPhone || "",
        deliveryAddress: "",
        deliveryLocation: null,
        paymentMethod: "COD",
        notesFromConversation: "",
        items: [{ productId: "", name: "", qty: 1, price: "" }],
      });
      setSummaryDirty(false);
      setOrderSummary("");
      await loadDetail();
    } catch (error) {
      setActionError(error.response?.data?.message || "Could not start a new order.");
    }
  };

  // Step 6 -> 7: confirm the draft and create the linked Lusuku order.
  const createOrder = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!canCreateOrder) return;

    const items = collectOrderItems();
    if (!items.length) {
      setActionError("Add at least one item before creating the order.");
      return;
    }

    try {
      setActionError("");
      setCreatingOrder(true);
      await apiConvertInboxConversationToOrder(vendorId, selectedId, {
        customerPhone: orderForm.customerPhone,
        deliveryAddress: {
          address: orderForm.deliveryAddress || "Conversation order",
          ...(orderForm.deliveryLocation?.latLng ? { latLng: orderForm.deliveryLocation.latLng } : {}),
          ...(orderForm.deliveryLocation?.locationName ? { locationName: orderForm.deliveryLocation.locationName } : {}),
          ...(orderForm.deliveryLocation?.locationId ? { locationId: orderForm.deliveryLocation.locationId } : {}),
        },
        paymentMethod: orderForm.paymentMethod,
        notesFromConversation: orderForm.notesFromConversation,
        items,
      });
      await loadDetail();
      await loadConversations();
      await loadConversationCounts();
    } catch (error) {
      setActionError(error.response?.data?.message || "Order could not be created.");
    } finally {
      setCreatingOrder(false);
    }
  };

  if (hqOnlyMessage) return <HeadOfficeInboxNotice />;

  if (!vendorId && !platformAdmin) {
    return (
      <Container fluid className="omni-page">
        <Card body className="omni-empty-state text-center py-5">
            <div className="omni-empty-icon"><FiInbox /></div>
            <h5 className="mt-3">Customer Chats</h5>
            <p className="text-muted">Sign in as a vendor or verified vendor staff member to use Customer Conversations.</p>
          </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="omni-page">
      {platformAdmin && (
        <Card className="omni-admin-selector">
          <Card.Body>
            <Row className="align-items-end g-2">
              <Col md={6} xl={4}>
                <Form.Label className="omni-field-label">
                  <FiBriefcase /> Viewing vendor inbox
                </Form.Label>
                <AppSelect block
                  className="omni-select"
                  value={selectedVendorId || ""}
                  onChange={(event) => setSelectedVendorId(event.target.value)}
                  disabled={vendorLoading}
                >
                  <option value="">
                    {vendorLoading ? "Loading vendors..." : "Select a vendor"}
                  </option>
                  {adminVendors.map((vendor) => (
                    <option key={`${vendor.vendorType || vendor.operationMode}-${vendor._id}`} value={vendor._id}>
                      {vendor.name || "Unnamed vendor"} ({vendor.vendorType || vendor.operationMode || "Vendor"})
                    </option>
                  ))}
                </AppSelect>
              </Col>
              <Col className="omni-admin-note">
                Lusuku admins can inspect one vendor inbox at a time. WhatsApp routing remains tied to each vendor&apos;s connected phone number.
              </Col>
            </Row>
            {vendorError && <div className="text-danger small mt-2">{vendorError}</div>}
          </Card.Body>
        </Card>
      )}

      {platformAdmin && !vendorId ? (
        <Card body className="omni-empty-state text-center py-5">
          <div className="omni-empty-icon"><FiInbox /></div>
          <h5 className="mt-3">Select a vendor inbox</h5>
          <p className="text-muted mb-0">Choose a vendor above to view their WhatsApp and Lusuku App conversations.</p>
        </Card>
      ) : (
      <>
      <div className="omni-mobile-tabs" role="tablist">
        <button type="button" className={`omni-mobile-tab ${mobileTab === "list" ? "active" : ""}`} onClick={() => setMobileTab("list")}>
          Chats{unreadCount > 0 && <span className="omni-tab-badge">{unreadCount}</span>}
        </button>
        <button type="button" className={`omni-mobile-tab ${mobileTab === "chat" ? "active" : ""}`} onClick={() => setMobileTab("chat")} disabled={!selectedId}>Conversation</button>
        <button type="button" className={`omni-mobile-tab ${mobileTab === "details" ? "active" : ""}`} onClick={() => setMobileTab("details")} disabled={!selectedId}>Details</button>
      </div>
      {socketDown && (
        <div className="omni-reconnect-bar" role="status">
          <Spinner animation="border" size="sm" />
          <span>Connection lost — reconnecting. New messages will sync automatically once restored.</span>
        </div>
      )}
      <div className={`omni-workspace omni-resizable omni-mobile-${mobileTab}`} ref={workspaceRef}>
        <div className="omni-col omni-col-list" style={{ width: `${panelWidths.left}%` }}>
          <Card className="omni-panel omni-thread-panel">
            <Card.Header className="omni-panel-header">
              <div>
                <div className="omni-eyebrow">Inbox</div>
                <strong>Customer Chats</strong>
              </div>
              <div className="d-flex align-items-center omni-header-actions">
                {loading && <Spinner size="sm" />}
                <button
                  type="button"
                  className={`omni-automation-pill ${offlineAlertsOn ? "is-on" : "is-off"}`}
                  onClick={openAutomationModal}
                  title="Inbox automation settings"
                >
                  {offlineAlertsOn ? (
                    <>
                      <FiBell />
                      <span>Offline alerts on</span>
                      {offlineAlert?.whatsappNumber && (
                        <span className="omni-automation-pill-num">
                          {maskWhatsAppNumber(offlineAlert.whatsappNumber)}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <FiBell />
                      <span>Offline alerts off</span>
                    </>
                  )}
                  <FiSettings className="omni-automation-pill-gear" />
                </button>
                <Button type="button" size="sm" className="omni-secondary-action omni-new-chat-btn" onClick={openNewChat} title="Start a new WhatsApp conversation">
                  <FiPlus /> New chat
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="omni-thread-body">
              <div className="omni-search-row">
                <FiSearch className="omni-search-icon" aria-hidden="true" />
                <Form.Control
                  size="sm"
                  className="omni-search-input"
                  placeholder="Search name or phone"
                  aria-label="Search conversations by name or phone"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="omni-link-btn omni-search-clear"
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <FiX />
                  </button>
                )}
              </div>
              <div className="omni-filter-row">
                {filterOptions.map((option) => {
                  const count = filterCounts[option.label] || 0;
                  const needsAttention = (option.assignedTo === "unassigned" || option.unread) && count > 0;
                  return (
                    <Button
                      key={option.label}
                      size="sm"
                      className={`omni-filter-pill ${activeFilter.label === option.label ? "is-active" : ""} ${needsAttention ? "needs-attention" : ""}`}
                      variant="light"
                      onClick={() => setActiveFilter(option)}
                    >
                      <span>{option.label}</span>
                      {count > 0 && (
                        <span className={`omni-filter-count ${needsAttention ? "attention" : ""}`}>
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>
              <div className="omni-thread-list" ref={attachThreadListRef}>
                {visibleConversations.map((conversation) => {
                  const meta = channelMeta(conversation.channel);
                  const selected = selectedId === conversation._id;
                  const unread = isUnreadConversation(conversation);
                  return (
                  <button
                    key={conversation._id}
                    type="button"
                    className={`omni-thread-item ${selected ? "is-selected" : ""} ${unread ? "has-unread" : ""}`}
                    onClick={() => setSelectedId(conversation._id)}
                    onMouseEnter={() => prefetchDetail(conversation._id)}
                    onFocus={() => prefetchDetail(conversation._id)}
                  >
                    <CustomerAvatar
                      name={resolveCustomerName(conversation)}
                      phone={conversation.customerPhone}
                      avatarUrl={conversation.customerAvatarUrl}
                    />
                    <span className="omni-thread-content">
                      <span className="omni-thread-topline">
                        <strong>{resolveCustomerName(conversation)}</strong>
                        <span className={`omni-channel-badge ${conversation.channel === "whatsapp" ? "whatsapp" : "app"}`}>
                          {meta.icon} {meta.label}
                        </span>
                      </span>
                      <span className="omni-thread-meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span className={`omni-status-dot ${statusTone(conversation.status)}`} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {statusLabel(conversation.status)} · {ownerLabel(conversation)}
                        </span>
                        {formatThreadTime(conversation) && (
                          <span className="omni-thread-time" style={{ marginLeft: "auto", flex: "0 0 auto", color: "#94a3b8" }}>
                            {formatThreadTime(conversation)}
                          </span>
                        )}
                      </span>
                      <span className="omni-thread-preview">{conversation.lastMessageId?.body || "No messages yet"}</span>
                    </span>
                    {unread && <span className="omni-unread-dot" title="Unread customer reply" />}
                  </button>
                  );
                })}
                {!conversations.length && !loading && (
                  <div className="omni-empty-inline">
                    <div className="omni-empty-icon small"><FiMessageCircle /></div>
                    <div>No conversations match this view.</div>
                  </div>
                )}
                {conversations.length > 0 && !visibleConversations.length && (
                  <div className="omni-empty-inline">
                    <div className="omni-empty-icon small"><FiSearch /></div>
                    <div>No loaded conversations match &quot;{searchQuery.trim()}&quot;.</div>
                  </div>
                )}
                {hasMore && (
                  <div className="text-center py-2">
                    <Button
                      size="sm"
                      className="omni-secondary-button"
                      variant="light"
                      onClick={loadMoreConversations}
                      disabled={loadingMore}
                    >
                      {loadingMore ? <Spinner size="sm" /> : "Load more"}
                    </Button>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="omni-resizer" onMouseDown={startPanelDrag("left")} title="Drag to resize" />

        <div className="omni-col omni-col-chat" style={{ width: `${centerWidth}%` }}>
          <Card className="omni-panel omni-chat-panel">
            <Card.Header className="omni-chat-header">
              <div className="d-flex align-items-center gap-2">
                <CustomerAvatar
                  name={headerCustomerName}
                  phone={headerCustomerPhone}
                  avatarUrl={headerAvatarUrl}
                  size={44}
                />
                <div>
                  <strong>{headerCustomerName}</strong>
                  <div className="omni-chat-subtitle">
                    <FaWhatsapp className="omni-chat-header-channel" title="WhatsApp" />
                    {headerCustomerPhone || "No conversation selected"}
                    {headerConversation ? ` · ${ownerLabel(headerConversation)}` : ""}
                  </div>
                </div>
              </div>
              {headerConversation && (
                <span className={`omni-status-chip ${statusTone(headerConversation.status)}`}>
                  {statusLabel(headerConversation.status)}
                </span>
              )}
            </Card.Header>
            <Card.Body className="omni-chat-body">
              <div className="omni-message-scroll" ref={messageScrollRef} onScroll={handleMessageScroll}>
                {detailLoading && (
                  <div className="omni-chat-skeleton" aria-busy="true" aria-label="Loading conversation">
                    {[35, 60, 48, 72, 40, 55, 65].map((w, i) => (
                      <div key={i} className={`omni-skeleton-row ${i % 2 ? "out" : "in"}`}>
                        <div className="omni-skeleton-bubble" style={{ width: `${w}%` }} />
                      </div>
                    ))}
                  </div>
                )}
                {!detailLoading && !detail?.messages?.length && (
                  <div className="omni-chat-empty">
                    <FiMessageCircle />
                    <span>Messages will appear here when a conversation is selected.</span>
                  </div>
                )}
                {!detailLoading && (
                <div className={`omni-message-track ${chatReady ? "is-ready" : ""}`}>
                {detail?.messages?.map((message, mi, allMsgs) => {
                  const isOut = message.direction === "outbound";
                  const media = message.media || {};
                  const mediaLink = media.link || media.url || null;

                  // WhatsApp-style date separators. Threads are permanent per
                  // customer and span weeks — bubbles only show HH:MM, so
                  // without day chips "12:54 AM" (June 28) under "10:01 AM"
                  // (July 8) reads like the timeline is out of order.
                  const dayOf = (ts) => (ts ? new Date(ts).toDateString() : "");
                  const msgDay = dayOf(message.createdAt);
                  const prevDay = mi > 0 ? dayOf(allMsgs[mi - 1]?.createdAt) : null;
                  const showDayChip = Boolean(msgDay) && msgDay !== prevDay;
                  const dayChipLabel = (() => {
                    if (!showDayChip) return "";
                    const today = new Date().toDateString();
                    const yesterday = new Date(Date.now() - 86_400_000).toDateString();
                    if (msgDay === today) return "Today";
                    if (msgDay === yesterday) return "Yesterday";
                    return new Date(message.createdAt).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
                  })();

                  let mediaNode = null;
                  if (message.messageType === "image") {
                    mediaNode = mediaLink ? (
                      <img
                        src={mediaLink}
                        alt="Shared attachment"
                        className="omni-media omni-media-clickable"
                        onClick={() => setLightboxUrl(mediaLink)}
                      />
                    ) : (
                      <div className="omni-media-placeholder"><FiImage /> Image unavailable</div>
                    );
                  } else if (message.messageType === "audio") {
                    mediaNode = mediaLink ? (
                      <audio controls src={mediaLink} className="omni-audio" />
                    ) : (
                      <div className="omni-media-placeholder"><FiMusic /> Audio unavailable</div>
                    );
                  } else if (message.messageType === "video") {
                    mediaNode = mediaLink ? (
                      <video controls src={mediaLink} className="omni-video" />
                    ) : (
                      <div className="omni-media-placeholder"><FiVideo /> Video unavailable</div>
                    );
                  } else if (message.messageType === "document") {
                    mediaNode = mediaLink ? (
                      <a href={mediaLink} target="_blank" rel="noreferrer" className={isOut ? "text-white" : ""}>
                        <FiFile /> {media.filename || "Document"}
                      </a>
                    ) : (
                      <div className="omni-media-placeholder"><FiFile /> Document unavailable</div>
                    );
                  } else if (message.messageType === "location" && media.latitude != null) {
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${media.latitude},${media.longitude}`;
                    mediaNode = (
                      <a href={mapsUrl} target="_blank" rel="noreferrer" className={`omni-location ${isOut ? "out" : "in"}`}>
                        <FiMapPin />
                        <span className="omni-location-text">
                          <strong>{media.name || "Shared location"}</strong>
                          {media.address ? <span className="omni-location-addr">{media.address}</span> : null}
                          <span className="omni-location-open">Open in Maps</span>
                        </span>
                      </a>
                    );
                  }

                  // Interactive-message rendering: show the agent what the
                  // customer actually saw (header/body/buttons) instead of a
                  // terse summary line — and tag inbound menu picks.
                  const interactivePayload = message.interactive || null;
                  const showInteractiveCard =
                    isOut && interactivePayload && ["list", "button", "product_list"].includes(interactivePayload.type);
                  const inboundMenuPick =
                    !isOut && interactivePayload && ["list_reply", "button_reply"].includes(interactivePayload.kind);
                  const interactiveRowCount = showInteractiveCard
                    ? (interactivePayload.action?.sections || []).reduce((n, s) => n + (s.rows || []).length, 0)
                    : 0;

                  let tickNode = null;
                  if (isOut) {
                    if (message.status === "sending") {
                      tickNode = <span className="omni-tick" title="Sending"><FiClock /></span>;
                    } else if (message.status === "read") {
                      tickNode = <span className="omni-tick read" title="Read"><FiCheck /><FiCheck /></span>;
                    } else if (message.status === "delivered") {
                      tickNode = <span className="omni-tick" title="Delivered"><FiCheck /><FiCheck /></span>;
                    } else if (message.status === "sent") {
                      tickNode = <span className="omni-tick" title="Sent"><FiCheck /></span>;
                    } else if (message.status === "failed") {
                      tickNode = <span className="omni-tick failed" title="Failed">Failed</span>;
                    }
                  }

                  return (
                    <React.Fragment key={message._id}>
                    {showDayChip && (
                      <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 6px" }}>
                        <span style={{ background: "rgba(0,0,0,0.08)", borderRadius: 12, padding: "2px 12px", fontSize: "0.7rem", color: "#5f6b7a", fontWeight: 600 }}>
                          {dayChipLabel}
                        </span>
                      </div>
                    )}
                    <div
                      className={`omni-message-row ${isOut ? "out" : "in"}`}
                    >
                      <div className={`omni-bubble ${isOut ? "out" : "in"}`}>
                        {message.contextSummary && (
                          <div className="omni-quote">
                            <span className="omni-quote-label">{message.contextSummary.direction === "outbound" ? "You" : "Customer"}</span>
                            <span className="omni-quote-text">{message.contextSummary.body || `[${message.contextSummary.messageType}]`}</span>
                          </div>
                        )}
                        {mediaNode}
                        {showInteractiveCard ? (
                          <div>
                            {interactivePayload.header?.text && (
                              <div style={{ fontWeight: 700, marginBottom: 2 }}>{interactivePayload.header.text}</div>
                            )}
                            {interactivePayload.body?.text && (
                              <div className="omni-message-text">{interactivePayload.body.text}</div>
                            )}
                            {interactivePayload.type === "list" ? (
                              <div style={{ marginTop: 6, fontSize: "0.78rem", opacity: 0.9, display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 12, padding: "3px 10px" }}>
                                <FiList /> {interactivePayload.action?.button || "View"} · {interactiveRowCount} options
                              </div>
                            ) : (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                                {(interactivePayload.action?.buttons || []).map((b, i) => (
                                  <span key={i} style={{ border: "1px solid rgba(0,0,0,0.15)", borderRadius: 12, padding: "2px 10px", fontSize: "0.78rem" }}>
                                    {b.reply?.title}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {inboundMenuPick && (
                              <div style={{ fontSize: "0.68rem", opacity: 0.65, marginBottom: 2 }}>
                                <FiList style={{ verticalAlign: "-2px" }} /> Selected from menu
                              </div>
                            )}
                            {message.body && (
                              <div className="omni-message-text">{message.body}</div>
                            )}
                          </>
                        )}
                        {!mediaNode && !message.body && !showInteractiveCard && (
                          <div className="small fst-italic opacity-75">[{message.messageType}]</div>
                        )}
                        <div className="omni-message-time">
                          {tickNode}
                          {message.createdAt ? new Date(message.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                        {message.reactionEmoji ? (
                          <span className={`omni-reaction-chip ${isOut ? "out" : "in"}`}>{message.reactionEmoji}</span>
                        ) : null}
                        {/* Hover actions: reply + react */}
                        <div className="omni-bubble-actions">
                          <button type="button" title="Reply" onClick={() => setReplyTarget(message)}><FiCornerUpLeft /></button>
                          <button type="button" title="React" onClick={() => setReactPickerId(reactPickerId === message._id ? null : message._id)}><FiSmile /></button>
                        </div>
                        {reactPickerId === message._id && (
                          <div className="omni-reaction-picker">
                            {REACTION_EMOJIS.map((e) => (
                              <button type="button" key={e} onClick={() => handleReact(message, e)}>{e}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                {typingPeer && (
                  <div className="omni-message-row in">
                    <div className="omni-bubble in omni-typing-bubble">
                      <span className="omni-typing-dot" /><span className="omni-typing-dot" /><span className="omni-typing-dot" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
                </div>
                )}
                {showJumpPill && (
                  <button type="button" className="omni-jump-pill" onClick={jumpToLatest} aria-label="Jump to the newest message">
                    New message <FiArrowDown />
                  </button>
                )}
              </div>
              <ComposerPopover open={showQuickReplies} onClose={() => setShowQuickReplies(false)}>
                  <div className="omni-quick-replies-head">
                    <span><FiZap /> Quick replies</span>
                    <button type="button" className="omni-link-btn" onClick={() => setShowQuickReplies(false)}>Close</button>
                  </div>
                  <div className="omni-quick-replies-list">
                    {dispositions.length === 0 && (
                      <div className="small text-muted px-2 py-1">No quick replies yet. Add one below.</div>
                    )}
                    {dispositions.map((d) => (
                      <div key={d._id} className="omni-quick-reply-item">
                        <button type="button" className="omni-quick-reply-insert" onClick={() => insertDisposition(d.body)} title={d.body}>
                          <strong>{d.title}</strong>
                          <span className="omni-quick-reply-preview">{d.body}</span>
                        </button>
                        <button type="button" className="omni-link-btn text-danger" onClick={() => removeDisposition(d._id)} title="Delete"><FiTrash2 /></button>
                      </div>
                    ))}
                  </div>
                  <div className="omni-quick-reply-add">
                    <Form.Control size="sm" className="omni-control mb-1" placeholder="Title (e.g. Greeting)" value={newDisposition.title} onChange={(e) => setNewDisposition({ ...newDisposition, title: e.target.value })} />
                    <Form.Control size="sm" as="textarea" rows={2} className="omni-control mb-1" placeholder="Message text" value={newDisposition.body} onChange={(e) => setNewDisposition({ ...newDisposition, body: e.target.value })} />
                    <Button type="button" size="sm" className="omni-secondary-action" onClick={addDisposition} disabled={!newDisposition.title.trim() || !newDisposition.body.trim()}>
                      <FiPlus /> Add quick reply
                    </Button>
                  </div>
              </ComposerPopover>
              <ComposerPopover open={showEmoji} onClose={() => setShowEmoji(false)} className="omni-emoji-palette">
                  {QUICK_EMOJIS.map((e) => (
                    <button type="button" key={e} className="omni-emoji-btn" onClick={() => insertEmoji(e)}>{e}</button>
                  ))}
              </ComposerPopover>
              <ComposerPopover open={showProductPicker} onClose={() => setShowProductPicker(false)}>
                  <div className="omni-quick-replies-head">
                    <span><FiShoppingBag /> Send a product</span>
                    <button type="button" className="omni-link-btn" onClick={() => setShowProductPicker(false)}>Close</button>
                  </div>
                  {/* Interactive menu: customer picks items natively in WhatsApp
                      and the selection returns as an order draft on this thread. */}
                  <div className="px-2 pb-2">
                    <Button
                      type="button"
                      size="sm"
                      className="omni-secondary-action w-100"
                      onClick={sendMenuToCustomer}
                      disabled={sendingMenu || windowExpired}
                      title="Customer browses the menu inside WhatsApp and their picks come back as an order draft"
                    >
                      {sendingMenu ? <Spinner animation="border" size="sm" /> : <><FiShoppingBag /> Send full menu (customer picks)</>}
                    </Button>
                  </div>
                  <div className="omni-quick-replies-list">
                    {catalogLoading && (
                      <div className="omni-loading px-2 py-2"><Spinner size="sm" /> Loading products</div>
                    )}
                    {!catalogLoading && catalogError && (
                      <button type="button" className="omni-link-btn text-danger px-2 py-1" onClick={loadCatalog}>{catalogError}</button>
                    )}
                    {!catalogLoading && !catalogError && catalog.length === 0 && (
                      <div className="small text-muted px-2 py-1">No products available for this vendor.</div>
                    )}
                    {!catalogLoading && catalog.map((p) => (
                      <button key={p._id} type="button" className="omni-quick-reply-insert omni-product-item" onClick={() => sendProductToChat(p)} disabled={sendingProduct}>
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            className="omni-product-thumb"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <span className="omni-product-thumb omni-product-thumb-empty"><FiImage /></span>
                        )}
                        <span className="omni-product-info">
                          <strong>{p.name}</strong>
                          <span className="omni-quick-reply-preview">{p.price != null ? `UGX ${Number(p.price).toLocaleString()}` : ""}</span>
                        </span>
                      </button>
                    ))}
                  </div>
              </ComposerPopover>
              <ComposerPopover open={showLocationPicker} onClose={() => { setShowLocationPicker(false); setPickedLocation(null); }}>
                  <div className="omni-quick-replies-head">
                    <span><FiMapPin /> Send a location</span>
                    <button type="button" className="omni-link-btn" onClick={() => { setShowLocationPicker(false); setPickedLocation(null); }}>Close</button>
                  </div>
                  <div className="px-2 py-2">
                    <AddressAutocomplete onChange={(loc) => setPickedLocation(loc || null)} />
                    {pickedLocation?.address && (
                      <div className="small text-muted mt-1">{pickedLocation.address}</div>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      className="omni-secondary-action mt-2"
                      onClick={sendLocation}
                      disabled={!pickedLocation?.latLng || sendingLocation}
                    >
                      {sendingLocation ? <Spinner animation="border" size="sm" /> : <><FiMapPin /> Send this location</>}
                    </Button>
                  </div>
              </ComposerPopover>
              <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*" style={{ display: "none" }} onChange={handleAttach} />
              <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" style={{ display: "none" }} onChange={handleAttachDoc} />
              {pendingAttachment && (
                <div className="omni-attach-preview">
                  <div className="omni-attach-preview-body">
                    {pendingAttachment.kind === "image" && pendingAttachment.previewUrl ? (
                      <img src={pendingAttachment.previewUrl} alt="Attachment preview" className="omni-attach-thumb" />
                    ) : pendingAttachment.kind === "video" && pendingAttachment.previewUrl ? (
                      <video src={pendingAttachment.previewUrl} className="omni-attach-thumb" muted />
                    ) : (
                      <span className="omni-attach-icon">
                        {pendingAttachment.kind === "audio" ? <FiMusic /> : pendingAttachment.kind === "video" ? <FiVideo /> : <FiFile />}
                      </span>
                    )}
                    <span className="omni-attach-info">
                      <span className="omni-attach-name">{pendingAttachment.file?.name || "Attachment"}</span>
                      <span className="omni-attach-meta">
                        {ATTACHMENT_KIND_LABELS[pendingAttachment.kind] || "File"}
                        {formatFileSize(pendingAttachment.file?.size) ? ` · ${formatFileSize(pendingAttachment.file?.size)}` : ""}
                        {attaching ? ` · Uploading ${uploadProgress}%` : " · Ready to send"}
                      </span>
                      {attaching && (
                        <span className="omni-attach-progress">
                          <span className="omni-attach-progress-fill" style={{ width: `${uploadProgress}%` }} />
                        </span>
                      )}
                    </span>
                  </div>
                  <button type="button" className="omni-link-btn" onClick={cancelAttachment} disabled={attaching} title="Remove attachment" aria-label="Remove attachment"><FiX /></button>
                </div>
              )}
              {replyTarget && (
                <div className="omni-reply-context">
                  <div className="omni-reply-context-body">
                    <span className="omni-reply-context-label">Replying to {replyTarget.direction === "outbound" ? "you" : "customer"}</span>
                    <span className="omni-reply-context-text">{replyTarget.body || `[${replyTarget.messageType}]`}</span>
                  </div>
                  <button type="button" className="omni-link-btn" onClick={() => setReplyTarget(null)} title="Cancel reply"><FiX /></button>
                </div>
              )}
              <Form onSubmit={sendReply} className="omni-reply-bar">
                <Button type="button" className="omni-icon-toggle" onClick={() => togglePopover("quick")} disabled={!selectedId || windowExpired} title="Quick replies" aria-label="Quick replies">
                  <FiZap />
                </Button>
                <Button type="button" className="omni-icon-toggle" onClick={() => togglePopover("emoji")} disabled={!selectedId || windowExpired} title="Emoji" aria-label="Insert emoji">
                  <FiSmile />
                </Button>
                <Button type="button" className="omni-icon-toggle" onClick={() => togglePopover("product")} disabled={!selectedId || windowExpired} title="Send a product" aria-label="Send a product">
                  <FiShoppingBag />
                </Button>
                <Button type="button" className="omni-icon-toggle" onClick={() => togglePopover("location")} disabled={!selectedId || windowExpired} title="Send a location" aria-label="Send a location">
                  <FiMapPin />
                </Button>
                <Button type="button" className="omni-icon-toggle" onClick={() => fileInputRef.current?.click()} disabled={!selectedId || windowExpired || attaching} title="Attach a photo, video, or voice note" aria-label="Attach a photo, video, or voice note">
                  {attaching ? <Spinner animation="border" size="sm" /> : <FiPaperclip />}
                </Button>
                <Button type="button" className="omni-icon-toggle" onClick={() => docInputRef.current?.click()} disabled={!selectedId || windowExpired || attaching} title="Attach a document" aria-label="Attach a document">
                  <FiFile />
                </Button>
                <Form.Control
                  as="textarea"
                  rows={1}
                  ref={replyInputRef}
                  className="omni-reply-input"
                  value={reply}
                  onChange={(event) => handleReplyChange(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter sends, Shift+Enter inserts a newline (WhatsApp-style).
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      if ((reply.trim() || pendingAttachment) && !windowExpired && !attaching) sendReply(event);
                    }
                  }}
                  placeholder={pendingAttachment ? "Add a caption (optional)" : "Reply to customer"}
                  disabled={!selectedId || windowExpired}
                />
                <Button type="submit" className="omni-send-button" disabled={(!reply.trim() && !pendingAttachment) || windowExpired || attaching} aria-label="Send message">
                  <FiSend />
                </Button>
              </Form>
              {windowExpired && (
                <div className="omni-alert">
                  <FiClock />
                  <span><strong>24-hour window closed.</strong> This customer hasn&apos;t messaged in over 24 hours. WhatsApp blocks free-form replies.{" "}
                  <button type="button" className="omni-link-btn" onClick={() => { setNewChatPhone(selectedConversation?.customerPhone || ""); openNewChat(); }}>Send a template message instead</button></span>
                </div>
              )}
              {actionError && <div className="text-danger small mt-2">{actionError}</div>}
            </Card.Body>
          </Card>
        </div>

        <div className="omni-resizer" onMouseDown={startPanelDrag("right")} title="Drag to resize" />

        <div className="omni-col omni-col-side" style={{ width: `${panelWidths.right}%` }}>
          <Card className="omni-side-card">
            <Card.Header className="omni-side-header"><FiUser /> <strong>Customer</strong></Card.Header>
            <Card.Body className="omni-side-body">
              <div className="omni-data-label">Channel</div>
              <div className="mb-3">
                {selectedConversation?.channel
                  ? (
                    <span className={`omni-channel-badge ${selectedConversation.channel === "whatsapp" ? "whatsapp" : "app"}`}>
                      {channelMeta(selectedConversation.channel).icon} {channelMeta(selectedConversation.channel).label}
                    </span>
                  )
                  : <span className="text-muted">None selected</span>
                }
              </div>
              <div className="omni-data-label">Phone</div>
              <div className="omni-data-value">{selectedConversation?.customerPhone || "-"}</div>

              {selectedId && (
                <div className="omni-contact-box mb-3">
                  <div className="omni-data-label">Saved name</div>
                  {editingContact ? (
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        size="sm"
                        className="omni-control"
                        placeholder="Contact name"
                        value={contactNameInput}
                        onChange={(e) => setContactNameInput(e.target.value)}
                      />
                      <Button type="button" size="sm" className="omni-primary-action" onClick={saveContact} disabled={savingContact || !contactNameInput.trim()}>
                        {savingContact ? "…" : "Save"}
                      </Button>
                      <button type="button" className="omni-link-btn" onClick={() => setEditingContact(false)}>Cancel</button>
                    </div>
                  ) : selectedConversation?.contactName ? (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">{selectedConversation.contactName}</span>
                      <button type="button" className="omni-link-btn" onClick={startEditContact}>Edit</button>
                    </div>
                  ) : (
                    <Button type="button" size="sm" className="omni-primary-action w-100" onClick={startEditContact}>
                      <FiUser /> Save as contact
                    </Button>
                  )}
                </div>
              )}
              {selectedConversation?.customerId ? (
                <div className="mb-3">
                  <span className="omni-soft-badge linked"><FiCheckCircle /> Lusuku account linked</span>
                </div>
              ) : (
                <div className="mb-3">
                  <span className="omni-soft-badge">No Lusuku account</span>
                </div>
              )}
              <div className="d-grid gap-2 mb-3">
                {(() => {
                  const claimed = Boolean(selectedConversation?.assignedToUserId);
                  const isClosed = ["closed", "resolved"].includes(selectedConversation?.status);
                  return (
                    <>
                      <Button
                        size="sm"
                        className="omni-primary-action"
                        disabled={!selectedId || claimed || isClosed}
                        onClick={() => apiAssignInboxConversation(vendorId, selectedId).then(loadDetail)}
                        title={claimed ? "Already claimed" : isClosed ? "Conversation is closed" : "Claim this conversation"}
                      >
                        <FiHeadphones /> {claimed ? `Claimed · ${ownerLabel(selectedConversation)}` : "Claim"}
                      </Button>
                      <Button
                        size="sm"
                        className="omni-secondary-action"
                        disabled={!selectedId || isClosed}
                        onClick={() => apiCloseInboxConversation(vendorId, selectedId, { dispositionId: "resolved" }).then(loadDetail)}
                      >
                        <FiArchive /> {isClosed ? "Closed" : "Close"}
                      </Button>
                    </>
                  );
                })()}
              </div>
              <div className="omni-section-title"><FiLink /> {activeTicket ? "Ticket" : "Create Ticket"}</div>
              {activeTicket ? (
                <div className="omni-ticket-card">
                  <div className="omni-ticket-card-head">
                    <span className="fw-semibold">{ticketCategoryLabel(activeTicket.category)}</span>
                    <span className={`omni-ticket-priority ${activeTicket.priority}`}>{activeTicket.priority}</span>
                  </div>
                  <div className="omni-ticket-card-meta">
                    <span className={`omni-soft-badge ${activeTicket.status === "open" ? "" : "linked"}`}>{ticketStatusLabel(activeTicket.status)}</span>
                    {activeTicket.createdAt && (
                      <span className="text-muted small">Opened {new Date(activeTicket.createdAt).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                    )}
                  </div>
                  {activeTicket.openingNotes && (
                    <div className="omni-ticket-notes">{activeTicket.openingNotes}</div>
                  )}
                  {activeTicket.resolutionNotes && (
                    <div className="omni-ticket-notes"><strong>Resolution:</strong> {activeTicket.resolutionNotes}</div>
                  )}
                  {["resolved"].includes(activeTicket.status) === false && (
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      className="omni-control mt-2 mb-2"
                      placeholder="Resolution note (added when you resolve or close)"
                      value={ticketResolution}
                      onChange={(e) => setTicketResolution(e.target.value)}
                      disabled={Boolean(ticketActionBusy)}
                    />
                  )}
                  {ticketError && <div className="text-danger small mb-2">{ticketError}</div>}
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {["open", "assigned"].includes(activeTicket.status) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-warning"
                        onClick={() => updateTicket("escalated")}
                        disabled={Boolean(ticketActionBusy)}
                      >
                        {ticketActionBusy === "escalated" ? <Spinner animation="border" size="sm" /> : "Escalate"}
                      </Button>
                    )}
                    {["open", "assigned", "escalated"].includes(activeTicket.status) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-success"
                        onClick={() => updateTicket("resolved")}
                        disabled={Boolean(ticketActionBusy)}
                      >
                        {ticketActionBusy === "resolved" ? <Spinner animation="border" size="sm" /> : "Resolve"}
                      </Button>
                    )}
                    {activeTicket.status === "resolved" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => updateTicket("open")}
                        disabled={Boolean(ticketActionBusy)}
                      >
                        {ticketActionBusy === "open" ? <Spinner animation="border" size="sm" /> : "Reopen"}
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      className="omni-secondary-action"
                      onClick={() => updateTicket("closed")}
                      disabled={Boolean(ticketActionBusy)}
                    >
                      {ticketActionBusy === "closed" ? <Spinner animation="border" size="sm" /> : "Close ticket"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Form
                  onSubmit={(e) => { e.preventDefault(); createTicket(); }}
                >
                  <AppSelect block
                    size="sm"
                    className="omni-control mb-2"
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm((t) => ({ ...t, category: e.target.value }))}
                    disabled={!selectedId || ticketSubmitting}
                  >
                    <option value="support">Support</option>
                    <option value="complaint">Complaint</option>
                    <option value="refund">Refund</option>
                    <option value="wrong_item">Wrong Item</option>
                    <option value="missing_item">Missing Item</option>
                    <option value="delivery_issue">Delivery Issue</option>
                    <option value="payment_issue">Payment Issue</option>
                    <option value="other">Other</option>
                  </AppSelect>
                  <AppSelect block
                    size="sm"
                    className="omni-control mb-2"
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm((t) => ({ ...t, priority: e.target.value }))}
                    disabled={!selectedId || ticketSubmitting}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </AppSelect>
                  <Form.Control
                    size="sm"
                    as="textarea"
                    rows={2}
                    className="omni-control mb-2"
                    placeholder="Add a note for context (optional)"
                    value={ticketForm.notes}
                    onChange={(e) => setTicketForm((t) => ({ ...t, notes: e.target.value }))}
                    disabled={!selectedId || ticketSubmitting}
                  />
                  {ticketError && <div className="text-danger small mb-2">{ticketError}</div>}
                  <Button type="submit" size="sm" className="omni-secondary-action w-100" disabled={!selectedId || ticketSubmitting}>
                    {ticketSubmitting ? <Spinner animation="border" size="sm" /> : <><FiPlus /> Create Ticket</>}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card className="omni-side-card">
            <Card.Header className="omni-side-header"><FiShoppingBag /> <strong>Order</strong></Card.Header>
            <Card.Body className="omni-side-body">
              {selectedConversation?.linkedOrderId ? (
                <div className="omni-created-order">
                  <span className="omni-soft-badge linked mb-2"><FiCheckCircle /> Order Created</span>
                  {createdOrderItems.map((it, i) => (
                    <div key={i} className="omni-created-line">
                      {it.imageUrl ? (
                        <img src={it.imageUrl} alt={it.name} className="omni-created-thumb" />
                      ) : (
                        <span className="omni-created-thumb omni-created-thumb-fallback"><FiShoppingBag /></span>
                      )}
                      <div className="omni-created-line-info">
                        <div className="omni-created-line-name">{it.name}</div>
                        <div className="small text-muted">{it.qty} × UGX {it.price.toLocaleString()}</div>
                      </div>
                      <div className="omni-created-line-total">UGX {(it.qty * it.price).toLocaleString()}</div>
                    </div>
                  ))}
                  <div className="omni-order-total my-2">Total: <strong>UGX {createdOrderTotal.toLocaleString()}</strong></div>
                  <div className="omni-data-label">Order ID</div>
                  <div className="text-break small mb-2">{selectedConversation.linkedOrderId}</div>

                  <div className="omni-payment-box mb-2">
                    {paymentConfirmed ? (
                      <div className="omni-soft-badge linked">
                        <FiCheckCircle /> Payment confirmed
                        {linkedOrder?.transactionId ? ` — Txn: ${linkedOrder.transactionId}` : ""}
                      </div>
                    ) : (
                      <>
                        <div className="omni-data-label">Confirm payment to unlock dispatch</div>
                        <div className="small text-muted mb-1">
                          Enter the transaction ID (MoMo/card reference, or the transfer reference if the
                          customer paid the vendor directly for a COD order).
                        </div>
                        <Form.Control
                          size="sm"
                          className="omni-control mb-1"
                          placeholder="Transaction ID"
                          value={txnInput}
                          onChange={(e) => setTxnInput(e.target.value)}
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="omni-primary-action w-100"
                          onClick={confirmPayment}
                          disabled={!txnInput.trim() || confirmingPayment}
                        >
                          {confirmingPayment ? "Confirming…" : "Confirm payment"}
                        </Button>
                        <div className="small text-warning mt-1">
                          Dispatch, rider assignment and delivery stay locked until payment is confirmed.
                        </div>
                      </>
                    )}
                  </div>

                  <Button type="button" size="sm" className="omni-secondary-action w-100 mb-2" onClick={copyCreatedOrder}>
                    <FiLink /> {orderCopied ? "Copied!" : "Copy order details"}
                  </Button>
                  <Button type="button" size="sm" className="omni-primary-action w-100 mb-2" onClick={shareCreatedOrder}>
                    <FiSend /> Share with customer
                  </Button>
                  <Button type="button" size="sm" className="omni-secondary-action w-100" onClick={startNewOrder} disabled={!canCreateOrder && !selectedConversation?.linkedOrderId}>
                    <FiPlus /> Start new order
                  </Button>
                </div>
              ) : (
                <Form onSubmit={(event) => event.preventDefault()}>
                  {hasDraft ? (
                    <div className="omni-soft-badge mb-2"><FiClock /> Draft awaiting customer confirmation</div>
                  ) : (
                    <div className="omni-data-label mb-2">Build a draft, confirm with the customer, then create the order.</div>
                  )}
                  {orderForm.items.map((item, index) => (
                    <div key={index} className="omni-order-item">
                      {catalog.length > 0 ? (
                        <AppSelect block
                          size="sm"
                          className="omni-control mb-2"
                          value={item.productId || ""}
                          onChange={(event) => selectCatalogItem(index, event.target.value)}
                          disabled={!canCreateOrder}
                        >
                          <option value="">— Select product —</option>
                          {catalog.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.name}{p.price != null ? ` — UGX ${p.price.toLocaleString()}` : ""}
                            </option>
                          ))}
                        </AppSelect>
                      ) : (
                        <Form.Control size="sm" className="omni-control mb-2" placeholder="Item name" value={item.name} onChange={(event) => updateOrderItem(index, "name", event.target.value)} disabled={!canCreateOrder} />
                      )}
                      <Row className="g-2">
                        <Col xs={5}>
                          <Form.Control className="omni-control" type="number" min="1" placeholder="Qty" value={item.qty} onChange={(event) => updateOrderItem(index, "qty", event.target.value)} disabled={!canCreateOrder} />
                        </Col>
                        <Col xs={7}>
                          <Form.Control className="omni-control" type="text" placeholder="Price" value={item.price !== "" && item.price != null ? `UGX ${Number(item.price).toLocaleString()}` : ""} readOnly title="Price comes from the product catalog" />
                        </Col>
                      </Row>
                      {orderForm.items.length > 1 && (
                        <Button type="button" size="sm" variant="link" className="px-0 text-danger" onClick={() => removeOrderItem(index)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" size="sm" className="omni-secondary-action mb-3" onClick={addOrderItem} disabled={!canCreateOrder}>
                    <FiPackage /> Add Item
                  </Button>
                  <Form.Control className="omni-control mb-2" placeholder="Customer phone" value={orderForm.customerPhone} onChange={(event) => setOrderForm({ ...orderForm, customerPhone: event.target.value })} disabled={!canCreateOrder} />
                  <div className="mb-2">
                    <AddressAutocomplete
                      disabled={!canCreateOrder}
                      onChange={(loc) =>
                        setOrderForm((current) => ({
                          ...current,
                          deliveryAddress: loc?.address || "",
                          deliveryLocation: loc || null,
                        }))
                      }
                    />
                    {orderForm.deliveryAddress && (
                      <div className="small text-muted mt-1">{orderForm.deliveryAddress}</div>
                    )}
                  </div>
                  <AppSelect block className="omni-control mb-2" value={orderForm.paymentMethod} onChange={(event) => setOrderForm({ ...orderForm, paymentMethod: event.target.value })} disabled={!canCreateOrder}>
                    <option value="COD">Cash on Delivery</option>
                    <option value="MOMO">MTN Mobile Money</option>
                    <option value="Airtel">Airtel Money</option>
                    <option value="Visa">Visa / Card</option>
                    <option value="CARD">Other Card</option>
                  </AppSelect>
                  <Form.Control as="textarea" rows={2} className="omni-control mb-2" placeholder="Order notes" value={orderForm.notesFromConversation} onChange={(event) => setOrderForm({ ...orderForm, notesFromConversation: event.target.value })} disabled={!canCreateOrder} />

                  {draftItems.length > 0 && (
                    <div className="omni-order-total mb-2">
                      Total: <strong>UGX {orderTotal.toLocaleString()}</strong>
                    </div>
                  )}

                  {/* Step 1: save the draft */}
                  <Button
                    type="button"
                    size="sm"
                    className="omni-secondary-action w-100 mb-2"
                    onClick={() => saveDraft()}
                    disabled={!canCreateOrder || savingDraft || !draftItems.length}
                  >
                    {savingDraft ? <Spinner size="sm" /> : <><FiFile /> {hasDraft ? "Update Draft" : "Save Draft"}</>}
                  </Button>

                  {/* Step 2: review + send summary to the customer */}
                  {hasDraft && (
                    <>
                      <div className="omni-section-title d-flex justify-content-between align-items-center">
                        <span><FiSend /> Customer Summary</span>
                        {summaryDirty && (
                          <Button
                            type="button"
                            size="sm"
                            variant="link"
                            className="px-0 omni-regenerate-link"
                            onClick={() => {
                              setSummaryDirty(false);
                              setOrderSummary("");
                            }}
                          >
                            Regenerate
                          </Button>
                        )}
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={7}
                        className="omni-control omni-summary-textarea mb-2"
                        value={effectiveSummary}
                        onChange={(event) => {
                          setSummaryDirty(true);
                          setOrderSummary(event.target.value);
                        }}
                        disabled={!canCreateOrder}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="omni-secondary-action w-100 mb-2"
                        onClick={sendSummaryToCustomer}
                        disabled={!canCreateOrder || sendingSummary || windowExpired || !effectiveSummary.trim()}
                      >
                        {sendingSummary ? <Spinner size="sm" /> : <><FiSend /> Send Summary to Customer</>}
                      </Button>
                      {savedDraft?.summarySentAt && (
                        <div className="omni-data-label mb-2">
                          <FiCheckCircle /> Summary sent {new Date(savedDraft.summarySentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </>
                  )}

                  {/* Step 3: confirm + create the Lusuku order */}
                  <Button
                    type="button"
                    size="sm"
                    className="omni-primary-action w-100"
                    onClick={createOrder}
                    disabled={!canCreateOrder || creatingOrder || !hasDraft}
                    title={!hasDraft ? "Save a draft and confirm with the customer first" : undefined}
                  >
                    {creatingOrder ? <Spinner size="sm" /> : <><FiCheck /> Create Lusuku Order</>}
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card className="omni-side-card">
            <Card.Header className="omni-side-header"><FaWhatsapp /> <strong>WhatsApp Channel</strong></Card.Header>
            <Card.Body className="omni-side-body">
              {activeWhatsApp ? (
                <div className="omni-connected-card">
                  <span className={`omni-soft-badge ${describeWhatsAppStatus(activeWhatsApp).className}`}>
                    <FiCheckCircle /> {describeWhatsAppStatus(activeWhatsApp).label}
                  </span>
                  <div className="omni-connected-number">{activeWhatsApp.displayPhoneNumber || activeWhatsApp.phoneNumberId}</div>
                  {activeWhatsApp.phoneNumberId && (
                    <div className="small text-muted">Phone ID: {activeWhatsApp.phoneNumberId}</div>
                  )}
                  {canRemoveNumber ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      className="omni-disconnect-button mt-2"
                      onClick={() => setPendingDisconnect(activeWhatsApp)}
                      disabled={disconnecting}
                    >
                      <FiTrash2 /> Remove number
                    </Button>
                  ) : (
                    <div className="small text-muted mt-2">Only a vendor admin or Lusuku superadmin can remove this number.</div>
                  )}
                </div>
              ) : (
                <p className="small text-muted">
                  Connect your WhatsApp Business number in one step — sign in with
                  Meta and authorize your number. No tokens to copy.
                </p>
              )}
              {!activeWhatsApp && (
                embeddedSignupReady ? (
                  <div className="mb-1">
                    <Button
                      size="sm"
                      className="omni-primary-action"
                      onClick={startEmbeddedSignup}
                      disabled={signupBusy}
                    >
                      <FaWhatsapp />{" "}
                      {signupBusy ? "Connecting…" : "Connect with WhatsApp"}
                    </Button>
                    <p className="small text-muted mt-2 mb-0">
                      You sign in with your own WhatsApp Business number — Meta
                      bills your account directly.
                    </p>
                    {signupError && (
                      <p className="small text-danger mt-2 mb-0">{signupError}</p>
                    )}
                  </div>
                ) : (
                  <p className="small text-warning mb-0">
                    WhatsApp onboarding is not configured yet. Please contact Lusuku support.
                  </p>
                )
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
      </>
      )}

      <Modal
        show={Boolean(pendingDisconnect)}
        onHide={() => !disconnecting && setPendingDisconnect(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!disconnecting}>
          <Modal.Title className="h5 d-flex align-items-center gap-2">
            <FiTrash2 className="text-danger" /> Remove WhatsApp number
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Remove{" "}
            <strong>
              {pendingDisconnect?.displayPhoneNumber || pendingDisconnect?.phoneNumberId}
            </strong>
            ?
          </p>
          <p className="text-muted small mb-0">
            Incoming messages to this number will stop and you&apos;ll need to reconnect it to
            use it again. Existing conversations are kept.
          </p>
          {actionError && <div className="text-danger small mt-2">{actionError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setPendingDisconnect(null)}
            disabled={disconnecting}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDisconnectWhatsApp} disabled={disconnecting}>
            {disconnecting ? (
              <><Spinner size="sm" className="me-2" /> Removing…</>
            ) : (
              <><FiTrash2 /> Remove number</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Agent-started conversation via approved template */}
      <Modal show={showNewChat} onHide={() => !startingChat && setShowNewChat(false)} centered>
        <Modal.Header closeButton={!startingChat}>
          <Modal.Title className="h5 d-flex align-items-center gap-2">
            <FaWhatsapp /> New WhatsApp conversation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span className="omni-microcopy mb-2">
            <strong>Why a template?</strong> WhatsApp only allows free typing within 24h of a
            customer's last message. To start a new chat or re-engage after that, Meta requires
            an <strong>approved template</strong>. Pick one below.
          </span>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">Customer phone (with country code)</Form.Label>
            <Form.Control
              placeholder="2567XXXXXXXX"
              value={newChatPhone}
              onChange={(e) => setNewChatPhone(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold">Template</Form.Label>
            {templatesLoading ? (
              <div className="text-muted small"><Spinner animation="border" size="sm" /> Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="text-muted small">{templatesError || "No approved templates found on this WABA."}</div>
            ) : (
              <AppSelect block
                value={selectedTemplate?.name || ""}
                onChange={(e) => pickTemplate(templates.find((t) => t.name === e.target.value))}
              >
                <option value="">— Select a template —</option>
                {templates.map((t) => (
                  <option key={`${t.name}-${t.language}`} value={t.name}>
                    {t.name} ({t.language}) · {t.category}
                  </option>
                ))}
              </AppSelect>
            )}
          </Form.Group>

          {pendingTemplates.length > 0 && (
            <div className="mb-2">
              <div className="small text-muted mb-1">Your submitted templates (awaiting Meta review)</div>
              {pendingTemplates.map((t) => (
                <div
                  key={`${t.name}-${t.language}`}
                  className="d-flex align-items-center justify-content-between py-1"
                  style={{ gap: 8 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="small fw-bold text-truncate">{t.name}</div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      {t.language}{t.category ? ` · ${t.category}` : ""}
                      {t.submissionStatus === "REJECTED" && t.reason ? ` — ${t.reason}` : ""}
                    </div>
                  </div>
                  <TemplateActionButton status={t.submissionStatus} />
                </div>
              ))}
              <div className="omni-microcopy mt-1">
                Awaiting Meta's review. Once approved they move to the list above and can be sent.
              </div>
            </div>
          )}

          <div className="mb-2">
            <button type="button" className="omni-link-btn" onClick={openTemplateLibrary}>
              {showLibrary ? "Hide" : "Don't have a template? Browse Lusuku starter templates"}
            </button>
          </div>
          {showLibrary && (
            <div className="mb-3" style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, maxHeight: 240, overflowY: "auto" }}>
              <span className="omni-microcopy mb-2">
                Pre-written, high-approval templates built for Lusuku's order flow. Submit one to
                your WhatsApp account → Meta reviews it (usually fast) → it appears in the list above.
              </span>
              <span className="omni-microcopy mb-2">
                <strong>Utility</strong> = order/payment/delivery updates (approve fastest).
                <strong> Marketing</strong> = promotional, reviewed more strictly.
              </span>
              {libraryLoading ? (
                <div className="text-muted small"><Spinner animation="border" size="sm" /> Loading…</div>
              ) : (
                library.map((tpl) => {
                  // Prefer the backend-resolved submission status so PENDING /
                  // APPROVED / REJECTED survives a page refresh and live-flips on
                  // the socket event. `submissionStatus` (added by the backend)
                  // wins; fall back to the legacy existingByName map, then to the
                  // purely-local optimistic submit flag.
                  const existing = libraryExisting[tpl.name];
                  const resolvedStatus =
                    tpl.submissionStatus ||
                    existing?.submissionStatus ||
                    existing?.status ||
                    null;
                  return (
                    <div key={tpl.key} className="d-flex align-items-start justify-content-between py-2" style={{ borderBottom: "1px solid #f1f5f9", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="small fw-bold">
                          {tpl.title}
                          {tpl.category === "MARKETING" && <span className="text-warning" style={{ fontSize: 11 }}> · marketing</span>}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11.5 }}>{tpl.bodyText}</div>
                      </div>
                      <TemplateActionButton
                        status={resolvedStatus}
                        submitting={submittingKey === tpl.key}
                        onSubmit={() => submitLibraryTemplate(tpl)}
                      />
                    </div>
                  );
                })
              )}
              {libraryNote && <div className="small mt-2" style={{ color: "#0a7d3c" }}>{libraryNote}</div>}
            </div>
          )}

          {selectedTemplate && (
            <div className="mb-2">
              <div className="small text-muted fst-italic mb-1">"{selectedTemplate.bodyText}"</div>
              {templateVars.length > 0 && (
                <span className="omni-microcopy mb-2">Fill each blank that Meta inserts into the message ({"{{1}}, {{2}}…"}).</span>
              )}
              {templateVars.map((v, i) => (
                <Form.Control
                  key={i}
                  className="mb-2"
                  placeholder={`Value for {{${i + 1}}}`}
                  value={v}
                  onChange={(e) => {
                    const next = [...templateVars];
                    next[i] = e.target.value;
                    setTemplateVars(next);
                  }}
                />
              ))}
            </div>
          )}
          {newChatError && <div className="text-danger small">{newChatError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowNewChat(false)} disabled={startingChat}>Cancel</Button>
          <Button
            className="omni-primary-action"
            onClick={startChat}
            disabled={startingChat || !newChatPhone.trim() || !selectedTemplate || templateVars.some((v) => !v.trim())}
          >
            {startingChat ? <><Spinner animation="border" size="sm" /> Sending…</> : <><FiSend /> Start conversation</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Inbox automation: offline alerts + chat distribution */}
      <Modal
        show={automationModalOpen}
        onHide={() => !savingAutomation && setAutomationModalOpen(false)}
        centered
        dialogClassName="omni-automation-modal"
      >
        <Modal.Header closeButton={!savingAutomation}>
          <Modal.Title className="h5 d-flex align-items-center gap-2">
            <FiSettings /> Inbox automation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Section A — Offline alerts */}
          <div className="omni-automation-section">
            <div className="omni-automation-section-title">
              <FiBell /> Offline alerts
            </div>
            <Form.Check
              type="switch"
              id="omni-offline-alert-switch"
              className="omni-automation-switch"
              label="Send me a WhatsApp alert when a customer messages and no one is online"
              checked={automationDraft.enabled}
              onChange={(e) =>
                setAutomationDraft((d) => ({ ...d, enabled: e.target.checked }))
              }
            />
            <Form.Group className="mt-3">
              <Form.Label className="small fw-bold">Your WhatsApp number</Form.Label>
              <Form.Control
                placeholder="2567XXXXXXXX"
                value={automationDraft.whatsappNumber}
                isInvalid={automationDraft.enabled && !draftNumberValid}
                onChange={(e) =>
                  setAutomationDraft((d) => ({ ...d, whatsappNumber: e.target.value }))
                }
              />
              {automationDraft.enabled && !draftNumberValid && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  Enter a valid WhatsApp number (with country code) to receive alerts.
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <p className="text-muted small mt-2 mb-2">
              Alerts are sent as a pre-approved WhatsApp template. You must approve the{" "}
              <strong>offline_chat_alert</strong> template in Meta once before alerts can be
              delivered.
            </p>
            <Form.Group className="mt-3">
              <Form.Label className="small fw-bold">
                Also alert me if a chat stays unanswered for this many minutes (even if I'm online)
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                style={{ maxWidth: 140 }}
                value={automationDraft.escalationMinutes}
                disabled={!automationDraft.enabled}
                onChange={(e) =>
                  setAutomationDraft((d) => ({ ...d, escalationMinutes: e.target.value }))
                }
              />
              <Form.Text className="text-muted">Set to 0 to turn this off.</Form.Text>
            </Form.Group>
            <details className="omni-automation-advanced">
              <summary>Advanced</summary>
              <Row className="mt-2">
                <Col xs={7}>
                  <Form.Label className="small fw-bold mb-1">Template name</Form.Label>
                  <Form.Control size="sm" value={automationDraft.templateName} readOnly />
                </Col>
                <Col xs={5}>
                  <Form.Label className="small fw-bold mb-1">Language</Form.Label>
                  <Form.Control size="sm" value={automationDraft.languageCode} readOnly />
                </Col>
              </Row>
            </details>
          </div>

          {/* Section B — Chat distribution */}
          <div className="omni-automation-section">
            <div className="omni-automation-section-title">
              <FiUser /> Chat distribution
            </div>
            <Form.Check
              type="radio"
              name="omni-distribution"
              id="omni-dist-round-robin"
              label="Round robin (default) — share new chats evenly across your team"
              checked={automationDraft.strategy === "round_robin"}
              onChange={() =>
                setAutomationDraft((d) => ({ ...d, strategy: "round_robin" }))
              }
            />
            <Form.Check
              type="radio"
              name="omni-distribution"
              id="omni-dist-manual"
              className="mt-2"
              label="Manual — I'll assign chats myself"
              checked={automationDraft.strategy === "manual"}
              onChange={() =>
                setAutomationDraft((d) => ({ ...d, strategy: "manual" }))
              }
            />
          </div>

          {automationError && (
            <div className="text-danger small mt-2">{automationError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setAutomationModalOpen(false)}
            disabled={savingAutomation}
          >
            Cancel
          </Button>
          <Button
            className="omni-primary-action"
            onClick={saveAutomationSettings}
            disabled={savingAutomation || automationSaveBlocked}
          >
            {savingAutomation ? (
              <><Spinner animation="border" size="sm" /> Saving…</>
            ) : (
              <><FiCheck /> Save</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image lightbox: chat images open full-size with a download path
          instead of being dead-end thumbnails. */}
      <Modal show={Boolean(lightboxUrl)} onHide={() => setLightboxUrl(null)} centered size="lg" contentClassName="omni-lightbox">
        <Modal.Body className="p-0 text-center omni-lightbox-body">
          {lightboxUrl && (
            <img src={lightboxUrl} alt="Full size attachment" className="omni-lightbox-img" />
          )}
        </Modal.Body>
        <Modal.Footer className="omni-lightbox-footer">
          {lightboxUrl && (
            <a href={lightboxUrl} target="_blank" rel="noreferrer" className="omni-link-btn">
              Open original in a new tab
            </a>
          )}
          <Button size="sm" className="omni-secondary-action" onClick={() => setLightboxUrl(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

/**
 * What a member limited to some branches sees instead of the inbox: customer
 * chats are head office's (4 Oct 2026). Their menu has no inbox entry; this
 * covers a bookmarked link, a bundle cached before the change, and a refusal
 * from the server. The same "Head office only" page every other head office
 * screen shows them (components/branch/HeadOfficeOnly.js).
 */
const HeadOfficeInboxNotice = () => (
  <Container fluid className="omni-page">
    <HeadOfficeOnly lead="Customer chats belong to head office." />
  </Container>
);

/** The inbox, or the head office notice for a member limited to some branches. */
const OmnichannelInboxGate = () => {
  const { isBranchScoped } = useVendorAccess();
  if (isBranchScoped) return <HeadOfficeInboxNotice />;
  return <OmnichannelInbox />;
};

export default OmnichannelInboxGate;
